import { BuilderElement, GlobalConfig, ElementType } from "@/types/builder";
import { generateId } from "@/utils/builder";
import {
  createMentionedInSection,
  createPartnerMenuSection,
  createListingSection,
} from "./template2";
import { color } from "framer-motion";

// ====================== Header Section ======================
const createHeaderSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    position: "sticky",
    top: "0",
    left: "0",
    width: "100%",
    zIndex: "1000",
    backgroundColor: "rgba(255,255,255,0.95)",
    boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
    backdropFilter: "blur(5px)",
    padding: "20px 0",
    transition: "all 0.3s",
  },
  responsiveStyles: {
    tablet: { padding: "15px 0" },
    mobile: { padding: "10px 0" },
  },
  className: "sticky",
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "container",
      styles: {
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: "64px",
        position: "relative",
        maxWidth: "1272px",
        margin: "0 auto",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { gap: "20px", padding: "0 24px" },
        mobile: { gap: "10px", padding: "0 10px" },
      },
      children: [
        // Logo/Brand
        {
          id: generateId(),
          type: "logo" as ElementType,
          className: "logo",
          styles: {
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
          },
          children: [
            {
              id: generateId(),
              type: "image" as ElementType,
              src: "/intellident/logo.png",
              styles: {
                width: "300px",
              },
            },
          ],
        },
        // Nav Links
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "nav-links",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "30px",
            marginLeft: "auto",
          },
          responsiveStyles: {
            tablet: { display: "none" },
            mobile: { display: "none" },
          },
          children: [
            {
              id: generateId(),
              type: "link" as ElementType,
              href: "#about-section",
              content: "About",
              styles: {
                textTransform: "capitalize",
                fontSize: "18px",
                fontWeight: "400",
                color: "forground",
                textDecoration: "none",
                transition: "color 0.2s",
                lineHeight: "22px",
              },
            },
            {
              id: generateId(),
              type: "link" as ElementType,
              href: "#how-it-works-section",
              content: "How it works",
              styles: {
                textTransform: "capitalize",
                fontSize: "18px",
                fontWeight: "400",
                color: "forground",
                textDecoration: "none",
                transition: "color 0.2s",
                lineHeight: "22px",
              },
            },
            {
              id: generateId(),
              type: "link" as ElementType,
              href: "#Ingredients",
              content: "Ingredients",
              styles: {
                textTransform: "capitalize",
                fontSize: "18px",
                fontWeight: "400",
                color: "forground",
                textDecoration: "none",
                transition: "color 0.2s",
                lineHeight: "22px",
              },
            },
          ],
        },
        // ORDER NOW Button
        {
          id: generateId(),
          type: "link" as ElementType,
          className: "header-cta",
          href: "#pricing",
          content: "ORDER NOW",
          styles: {
            textTransform: "uppercase",
            backgroundImage:
              "linear-gradient(180deg, #FFF4A3 0%, #FF8E07 100%)",
            color: "forground",
            padding: "0.5rem 1.3rem",
            fontSize: "15px",
            fontWeight: "800",
            borderRadius: "8px",
            marginLeft: "24px",
            transition: "all 0.2s",
            display: "inline-block",
            border: "1px solid secondary",
            boxShadow: "inset 0 0 7.81px 0 #FFF4A3",
          },
          responsiveStyles: {
            tablet: { display: "none" },
            mobile: { display: "none" },
          },
        },
        // Hamburger Button
        {
          id: generateId(),
          type: "button" as ElementType,
          content: "☰",
          interactionType: "popup",
          triggerPopup: "mobile-navigation",
          styles: {
            display: "none", // Hidden on desktop
            width: "44px",
            height: "44px",
            borderRadius: "50%",
            backgroundColor: "#F3F3F3",
            border: "none",
            alignItems: "center",
            justifyContent: "center",
            padding: "0",
            cursor: "pointer",
            marginLeft: "auto",
            fontSize: "18px",
            color: "#333",
            boxShadow: "0px 2px 8px #0000000a",
          },
          responsiveStyles: {
            tablet: {
              display: "flex",
            },
            mobile: {
              display: "flex",
            },
          },
        },
      ],
    },
  ],
});

// ====================== Hero Section (FortiGold Style) ======================
const createHeroSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "hero",
  styles: {
    backgroundImage:
      "linear-gradient(to right, #1e9dc8 60%, #65c6e7 80%, #37b4e1 90%)", // $primary-color
    padding: "60px 0",
    overflow: "hidden",
  },
  responsiveStyles: {
    tablet: { padding: "60px 24px 0" },
    mobile: { padding: "40px 15px 0" },
  },
  children: [
    // Main grid container
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "container",
      styles: {
        maxWidth: "1272px",
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        alignItems: "stretch",
        margin: "0 auto",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { gap: "36px" },
        mobile: { gap: "12px", gridTemplateColumns: "1fr" },
      },
      children: [
        // Left: Hero Content
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "hero-content",
          styles: { position: "relative", zIndex: "1" },
          children: [
            {
              id: generateId(),
              type: "heading" as ElementType,
              content:
                'A Healthier Mouth, A Sharper Mind - <span style="color:#051168;font-style:italic;">In Just Seconds a Day!</span>',
              styles: {
                fontSize: "60px",
                marginBottom: "32px",
                color: "background",
                fontWeight: "700",
                lineHeight: "1.2",
                letterSpacing: "-2.1px",
                textShadow: "0px 4px 9px 0px #000000",
              },
              typography: "Heading-1",
              responsiveStyles: {
                tablet: { fontSize: "32px" },
                mobile: {
                  fontSize: "34px",
                  textAlign: "left",
                  lineHeight: "55px",
                  marginBottom: "16px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content:
                "Experts Uncover a Natural Breakthrough That Supports Memory, Focus & Gum Health - No Harsh Chemicals, No Complicated Routines!",
              styles: {
                marginBottom: "30px",
                fontFamily: "Roboto, sans-serif",
                fontSize: "21px",
                lineHeight: "40px",
                fontWeight: "300",
                color: "background",
                letterSpacing: "-0.28px",
              },
              responsiveStyles: {
                tablet: { fontSize: "20px", lineHeight: "30px" },
                mobile: {
                  fontSize: "21px",
                  lineHeight: "32px",
                  textAlign: "left",
                },
              },
            },
            // Trust Badges
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "trust-badges",
              styles: {
                display: "flex",
                gap: "15px",
                marginTop: "30px",
              },
              responsiveStyles: {
                mobile: { gap: "5px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/certifications.png",
                  alt: "certifications",
                  styles: {
                    maxWidth: "100%",
                    width: "100%",
                    objectFit: "contain",
                  },
                  responsiveStyles: {
                    tablet: { maxWidth: "100%" },
                    mobile: { maxWidth: "100%" },
                  },
                },
              ],
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content: "Feel the Difference Starting Today!",
              styles: {
                marginTop: "32px",
                marginBottom: "30px",
                fontFamily: "pro-display-bold-italic, sans-serif",
                fontStyle: "italic",
                fontSize: "19x",
                lineHeight: "40px",
                fontWeight: "500",
                color: "quaternary",
                letterSpacing: "-0.28px",
              },
              responsiveStyles: {
                tablet: { fontSize: "18px", lineHeight: "30px" },
                mobile: {
                  fontSize: "19px",
                  lineHeight: "32px",
                  textAlign: "left",
                },
              },
            },
            // ORDER NOW BUTTON
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: { width: "100%" },
              children: [
                {
                  id: generateId(),
                  type: "button" as ElementType,
                  content: "ORDER NOW",
                  styles: {
                    width: "347px",
                    height: "64px",
                    borderRadius: "10px",
                    backgroundImage:
                      "linear-gradient(180deg, #FFF4A3 0%, #FF8E07 100%)",
                    border: "1px solid secondary",
                    boxShadow: "inset 0 0 7.81px 0 #FFF4A3",
                    color: "forground",
                    fontWeight: 800,
                    fontFamily: "Inter, sans-serif",
                    fontSize: "28px",
                    textTransform: "uppercase",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: 0,
                    marginBottom: "0",
                    marginTop: "10px",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    letterSpacing: "0.5px",
                  },
                  typography: "Button",
                  responsiveStyles: {
                    tablet: { width: "100%", fontSize: "22px", height: "56px" },
                    mobile: { width: "100%", fontSize: "20px", height: "48px" },
                  },
                },
              ],
            },
          ],
        },
        // Right: Hero Image
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "hero-image",
          styles: {
            textAlign: "center",
            display: "flex",
            justifyContent: "center",
            alignItems: "stretch",
            height: "100%",
          },
          children: [
            {
              id: generateId(),
              type: "image" as ElementType,
              src: "/intellident/hero-image.png",
              alt: "Turmeric Supplement Bottle",
              styles: {
                height: "100%",
                width: "auto",
                maxHeight: "100%",
                zIndex: "1",
                objectFit: "contain",
                display: "block",
              },
              responsiveStyles: {
                tablet: { height: "100%", width: "auto" },
                mobile: { height: "100%", width: "auto" },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Intellident Testimonials Section ======================
const createIntellidentTestimonialsSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "testimonials-bg",
  styles: {
    backgroundColor: "#157293",
    padding: "23px 0",
    width: "100%",
    minHeight: "200px",
  },
  responsiveStyles: {
    tablet: { padding: "23px 24px", minHeight: "250px" },
    mobile: { padding: "23px 16px", minHeight: "200px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "container",
      styles: {
        maxWidth: "1272px",
        margin: "0 auto",
      },
      responsiveStyles: {
        tablet: { padding: "0 24px" },
        mobile: { padding: "0 16px" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "testimonials-grid",
          styles: {
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: "32px",
            width: "100%",
          },
          responsiveStyles: {
            tablet: { gap: "24px" },
            mobile: { gridTemplateColumns: "1fr", gap: "24px" },
          },
          children: [
            // Testimonial 1
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "testimonial-card",
              styles: {
                backgroundColor: "background",
                width: "100%",
                padding: "28px 23px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: "11px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              },
              responsiveStyles: {
                tablet: { minHeight: "140px" },
                mobile: { padding: "16px", minHeight: "120px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/avatar 1.png",
                  alt: "Marcus T.",
                  className: "profile-img",
                  styles: {
                    width: "66px",
                    height: "66px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    aspectRatio: "1/1",
                  },
                  responsiveStyles: {
                    tablet: { width: "56px", height: "56px", left: "16px" },
                    mobile: { width: "48px", height: "48px", left: "12px" },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "content-area",
                  styles: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    justifyContent: "flex-start",
                  },
                  responsiveStyles: {
                    tablet: { gap: "4px" },
                    mobile: { gap: "3px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: "8px",
                        gap: "14px",
                        minHeight: "fit-content",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          className: "customer-name",
                          content: "Marcus T.",
                          styles: {
                            fontFamily: 'SFProDisplay-Bold", sans-serif',
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#414042",
                            margin: "0",
                            lineHeight: "1",
                            minHeight: "auto",
                          },
                          responsiveStyles: {
                            mobile: { fontSize: "15px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          className: "verified-label",
                          content: "Verified Intellident Pro™ User",
                          styles: {
                            fontFamily: "pro-display-light, sans-serif",
                            fontWeight: "300",
                            fontSize: "16px",
                            color: "#414042",
                            margin: "0",
                            lineHeight: "1",
                            fontStyle: "italic",
                          },
                          responsiveStyles: {
                            mobile: { fontSize: "11px" },
                          },
                        },
                      ],
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "testimonial-text",
                      content:
                        '"I was skeptical, but within days I could actually feel my memory and focus improving. Game changer."',
                      styles: {
                        fontFamily: "pro-display-regular, sans-serif",
                        fontSize: "16px",
                        color: "#414042",
                        lineHeight: "1.4",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "14px" },
                        mobile: { fontSize: "12px", lineHeight: "1.3" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        verticalAlign: "middle",
                        minHeight: "auto",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            gap: "2px",
                            minHeight: "auto",
                          },
                          children: [
                            ...Array(5)
                              .fill(0)
                              .map((_, i) => ({
                                id: generateId(),
                                type: "image" as ElementType,
                                src: "/intellident/star-icon.svg",
                                alt: "5 star rating",
                                styles: {
                                  width: "16px",
                                  height: "16px",
                                  aspectRatio: "1",
                                },
                              })),
                          ],
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          className: "rating-text",
                          content: "5/5",
                          styles: {
                            fontFamily: "pro-display-regular, sans-serif",
                            fontSize: "14px",
                            marginLeft: "8px",
                            fontWeight: "500",
                            verticalAlign: "middle",
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // Testimonial 2
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "testimonial-card",
              styles: {
                backgroundColor: "background",
                width: "100%",
                padding: "28px 23px",
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                gap: "11px",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              },
              responsiveStyles: {
                tablet: { minHeight: "140px" },
                mobile: { padding: "16px", minHeight: "120px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/avatar 2.png",
                  alt: "Marcus T.",
                  className: "profile-img",
                  styles: {
                    width: "66px",
                    height: "66px",
                    borderRadius: "50%",
                    objectFit: "cover",
                    aspectRatio: "1/1",
                  },
                  responsiveStyles: {
                    tablet: { width: "56px", height: "56px", left: "16px" },
                    mobile: { width: "48px", height: "48px", left: "12px" },
                  },
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "content-area",
                  styles: {
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    gap: "8px",
                    justifyContent: "flex-start",
                  },
                  responsiveStyles: {
                    tablet: { gap: "4px" },
                    mobile: { gap: "3px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        marginBottom: "8px",
                        gap: "14px",
                        minHeight: "fit-content",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          className: "customer-name",
                          content: "Nathalie S.",
                          styles: {
                            fontFamily: 'SFProDisplay-Bold", sans-serif',
                            fontSize: "16px",
                            fontWeight: "700",
                            color: "#414042",
                            margin: "0",
                            lineHeight: "1",
                            minHeight: "auto",
                          },
                          responsiveStyles: {
                            mobile: { fontSize: "15px" },
                          },
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          className: "verified-label",
                          content: "Verified Intellident Pro™ User",
                          styles: {
                            fontFamily: "pro-display-light, sans-serif",
                            fontWeight: "300",
                            fontSize: "16px",
                            color: "#414042",
                            margin: "0",
                            lineHeight: "1",
                            fontStyle: "italic",
                          },
                          responsiveStyles: {
                            mobile: { fontSize: "11px" },
                          },
                        },
                      ],
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "testimonial-text",
                      content:
                        '"My brain fog is gone. I feel clear, focused, and sharp - and all I did was add this to my morning routine."',
                      styles: {
                        fontFamily: "pro-display-regular, sans-serif",
                        fontSize: "16px",
                        color: "#414042",
                        lineHeight: "1.4",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "14px" },
                        mobile: { fontSize: "12px", lineHeight: "1.3" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                        verticalAlign: "middle",
                        minHeight: "auto",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            gap: "2px",
                            minHeight: "auto",
                          },
                          children: [
                            ...Array(5)
                              .fill(0)
                              .map((_, i) => ({
                                id: generateId(),
                                type: "image" as ElementType,
                                src: "/intellident/star-icon.svg",
                                alt: "5 star rating",
                                styles: {
                                  width: "16px",
                                  height: "16px",
                                  aspectRatio: "1",
                                },
                              })),
                          ],
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          className: "rating-text",
                          content: "5/5",
                          styles: {
                            fontFamily: "pro-display-regular, sans-serif",
                            fontSize: "14px",
                            marginLeft: "8px",
                            fontWeight: "500",
                            verticalAlign: "middle",
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Features Section ======================
const createFeaturesSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "features-sec",
  styles: {
    padding: "50px 60px",
    margin: "40px auto",
    maxWidth: "1272px",
    border: "4px solid white",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    backgroundImage:
      "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
  },
  responsiveStyles: {
    tablet: {
      margin: "30px 20px",
      padding: "45px 50px",
    },
    mobile: {
      padding: "40px 30px",
      margin: "20px 15px",
    },
  },
  children: [
    // Title
    {
      id: generateId(),
      type: "heading" as ElementType,
      className: "main-tit",
      content: "INTELLIDENT PRO™",
      styles: {
        color: "white",
        fontSize: "40px",
        textAlign: "center",
        marginBottom: "30px",
        fontFamily: 'SFProDisplay-Bold", sans-serif',
        letterSpacing: "0.5px",
        textShadow: "1px 1px 2px rgba(0, 0, 0, 0.3)",
        fontWeight: "700",
      },
      typography: "Heading-4",
      responsiveStyles: {
        tablet: { fontSize: "32px", marginBottom: "35px" },
        mobile: { fontSize: "26px", marginBottom: "25px" },
      },
    },
    // Features Grid
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "features-grid",
      styles: {
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        gridTemplateRows: "1fr 1fr",
        gap: "25px 60px",
        maxWidth: "800px",
        margin: "0 auto",
      },
      responsiveStyles: {
        tablet: {
          gap: "22px 50px",
          maxWidth: "650px",
        },
        mobile: {
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "1fr 1fr 1fr",
          gap: "20px 40px",
          maxWidth: "500px",
        },
      },
      children: [
        // Feature 1: Natural Formula
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "feature-i",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            fontFamily: "pro-display-medium, sans-serif",
            fontWeight: "500",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { gap: "12px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "check-i",
              content: "✓",
              styles: {
                width: "28px",
                height: "28px",
                backgroundImage:
                  "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
                color: "forground",
                border: "2px solid forground",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                margin: "0",
                padding: "0",
                textAlign: "center",
              },
              responsiveStyles: {
                tablet: {
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  borderWidth: "1.5px",
                },
                mobile: {
                  width: "18px",
                  height: "18px",
                  fontSize: "11px",
                  borderWidth: "1px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "feature-txt",
              content: "Natural Formula",
              styles: {
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "1.2",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "15px" },
              },
            },
          ],
        },
        // Feature 2: Easy To Use
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "feature-i",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            fontFamily: "pro-display-medium, sans-serif",
            fontWeight: "500",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { gap: "12px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "check-i",
              content: "✓",
              styles: {
                width: "28px",
                height: "28px",
                backgroundImage:
                  "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
                color: "forground",
                border: "2px solid forground",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                margin: "0",
                padding: "0",
                textAlign: "center",
              },
              responsiveStyles: {
                tablet: {
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  borderWidth: "1.5px",
                },
                mobile: {
                  width: "18px",
                  height: "18px",
                  fontSize: "11px",
                  borderWidth: "1px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "feature-txt",
              content: "Easy To Use",
              styles: {
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "1.2",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "15px" },
              },
            },
          ],
        },
        // Feature 3: Non-GMO
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "feature-i",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            fontFamily: "pro-display-medium, sans-serif",
            fontWeight: "500",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { gap: "12px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "check-i",
              content: "✓",
              styles: {
                width: "28px",
                height: "28px",
                backgroundImage:
                  "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
                color: "forground",
                border: "2px solid forground",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                margin: "0",
                padding: "0",
                textAlign: "center",
              },
              responsiveStyles: {
                tablet: {
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  borderWidth: "1.5px",
                },
                mobile: {
                  width: "18px",
                  height: "18px",
                  fontSize: "11px",
                  borderWidth: "1px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "feature-txt",
              content: "Non-GMO",
              styles: {
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "1.2",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "15px" },
              },
            },
          ],
        },
        // Feature 4: No Stimulants
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "feature-i",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            fontFamily: "pro-display-medium, sans-serif",
            fontWeight: "500",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { gap: "12px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "check-i",
              content: "✓",
              styles: {
                width: "28px",
                height: "28px",
                backgroundImage:
                  "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
                color: "forground",
                border: "2px solid forground",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                margin: "0",
                padding: "0",
                textAlign: "center",
              },
              responsiveStyles: {
                tablet: {
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  borderWidth: "1.5px",
                },
                mobile: {
                  width: "18px",
                  height: "18px",
                  fontSize: "11px",
                  borderWidth: "1px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "feature-txt",
              content: "No Stimulants",
              styles: {
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "1.2",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "15px" },
              },
            },
          ],
        },
        // Feature 5: Non-Habit Forming
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "feature-i",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            fontFamily: "pro-display-medium, sans-serif",
            fontWeight: "500",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { gap: "12px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "check-i",
              content: "✓",
              styles: {
                width: "28px",
                height: "28px",
                backgroundImage:
                  "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
                color: "forground",
                border: "2px solid forground",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                margin: "0",
                padding: "0",
                textAlign: "center",
              },
              responsiveStyles: {
                tablet: {
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  borderWidth: "1.5px",
                },
                mobile: {
                  width: "18px",
                  height: "18px",
                  fontSize: "11px",
                  borderWidth: "1px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "feature-txt",
              content: "Non-Habit Forming",
              styles: {
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "1.2",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "15px" },
              },
            },
          ],
        },
        // Feature 6: Gluten Free
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "feature-i",
          styles: {
            display: "flex",
            alignItems: "center",
            gap: "11px",
            fontFamily: "pro-display-medium, sans-serif",
            fontWeight: "500",
            cursor: "pointer",
          },
          responsiveStyles: {
            mobile: { gap: "12px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "check-i",
              content: "✓",
              styles: {
                width: "28px",
                height: "28px",
                backgroundImage:
                  "linear-gradient(135deg, #85d0e0 50%, #7dd3fc 20%, #87ceeb 30%)",
                color: "forground",
                border: "2px solid forground",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "20px",
                fontWeight: "bold",
                flexShrink: 0,
                cursor: "pointer",
                userSelect: "none",
                transition: "all 0.2s ease",
                margin: "0",
                padding: "0",
                textAlign: "center",
              },
              responsiveStyles: {
                tablet: {
                  width: "20px",
                  height: "20px",
                  fontSize: "12px",
                  borderWidth: "1.5px",
                },
                mobile: {
                  width: "18px",
                  height: "18px",
                  fontSize: "11px",
                  borderWidth: "1px",
                },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "feature-txt",
              content: "Gluten Free",
              styles: {
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "1.2",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "15px" },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Intellident Hero Section ======================
const createIntellidentHeroSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "hero-section linear-blue-gradient",
  styles: {
    backgroundImage: "linear-gradient(to right, #87ceeb, #1e9dc8)",
    display: "flex",
    alignItems: "center",
    padding: "100px 0px",
    overflow: "hidden",
  },
  responsiveStyles: {
    tablet: { padding: "30px 15px" },
    mobile: { padding: "25px 15px" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "content-wrapper",
      styles: {
        maxWidth: "1272px",
        width: "100%",
        margin: "0 auto",
        display: "grid",
        gridTemplateColumns: "40% 60%",
        gap: "0",
        alignItems: "flex-start",
        justifyContent: "center",
        boxSizing: "border-box",
        position: "relative",
        zIndex: "2",
      },
      responsiveStyles: {
        tablet: { gridTemplateColumns: "1fr", gap: "30px" },
        mobile: { gridTemplateColumns: "1fr", gap: "25px" },
      },
      children: [
        // Left: Image
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "image-section",
          styles: {
            position: "relative",
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
          },
          responsiveStyles: {
            mobile: { maxWidth: "280px", margin: "0 auto" },
          },
          children: [
            {
              id: generateId(),
              type: "image" as ElementType,
              src: "/intellident/intellident-woman.jpg",
              alt: "Woman with INTELLIDENT PRO supplement",
              className: "woman-image",
              styles: {
                width: "447px",
                height: "539px",
                borderRadius: "20px",
                objectFit: "cover",
                objectPosition: "left",
              },
              responsiveStyles: {
                tablet: { width: "320px", height: "400px" },
                mobile: { width: "280px", height: "350px" },
              },
            },
          ],
        },
        // Right: Text Content
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "text-section",
          styles: {
            color: "white",
            paddingLeft: "20px",
          },
          responsiveStyles: {
            tablet: { paddingLeft: "20px" },
            mobile: { textAlign: "center", paddingLeft: "0" },
          },
          children: [
            {
              id: generateId(),
              type: "heading" as ElementType,
              className: "main-title",
              content:
                "Unlock the Secret to a Sharper <br/> Mind & a Healthier Mouth",
              styles: {
                fontSize: "40px",
                fontWeight: "700",
                lineHeight: "1.1",
                marginBottom: "20px",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
              },
              typography: "Heading-4",
              responsiveStyles: {
                tablet: { fontSize: "38px" },
                mobile: { fontSize: "30px", marginBottom: "14px" },
              },
            },
            {
              id: generateId(),
              type: "heading" as ElementType,
              className: "subtitle",
              content:
                "Fuel Your Brain. Strengthen Your Smile. All in 8 Seconds.",
              styles: {
                fontSize: "21px",
                fontWeight: "500",
                marginBottom: "30px",
                fontFamily: "pro-display-medium, sans-serif",
              },
              responsiveStyles: {
                tablet: { fontSize: "20px" },
                mobile: { fontSize: "18px", marginBottom: "20px" },
              },
            },
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: { maxWidth: "570px", marginBottom: "20px" },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  className: "description-text",
                  content: `If you've ever struggled with <span class="font-pro-display-bold" style="font-weight: 700;">brain fog, forgetfulness, oral problems or mental fatigue</span>, you're not alone. For years, people have relied on temporary fixes - costly dental treatments or brain-boosting supplements that don't address the real problem.`,
                  styles: {
                    fontSize: "19px",
                    lineHeight: "1.4",
                    marginBottom: "20px",
                    fontFamily: "pro-display-regular, sans-serif",
                    fontWeight: "300",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: {
                      fontSize: "16px",
                      marginBottom: "16px",
                      lineHeight: "1.5",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  className: "description-text",
                  content: `<span class="font-pro-display-bold-italic">What if the key to lasting oral and cognitive health was already in your daily routine?</span>`,
                  styles: {
                    fontSize: "19px",
                    lineHeight: "1.4",
                    marginBottom: "20px",
                    fontFamily: "pro-display-regular, sans-serif",
                    fontWeight: "700",
                    fontStyle: "italic",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: {
                      fontSize: "16px",
                      marginBottom: "16px",
                      lineHeight: "1.5",
                    },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  className: "product-description",
                  content: `Introducing <span class="font-pro-display-bold" style="font-weight: 700;">INTELLIDENT PRO™</span> - a powerful, science-backed chewable that <span class="font-pro-display-bold" style="font-weight: 700;">helps restores gum health, freshens breath, and sharpens focus</span> in just <span class="font-pro-display-bold" style="font-weight: 700;">seconds a day</span>. Designed to support both <span class="font-pro-display-bold" style="font-weight: 700;">your oral microbiome and brain function</span>, this cutting-edge formula works at the root cause - helping you feel <span class="font-pro-display-bold" style="font-weight: 700;">clear-headed, confident, and refreshed every single day</span>.`,
                  styles: {
                    fontSize: "19px",
                    lineHeight: "1.4",
                    marginBottom: "20px",
                    fontFamily: "pro-display-regular, sans-serif",
                    fontWeight: "300",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: {
                      fontSize: "16px",
                      marginBottom: "16px",
                      lineHeight: "1.5",
                    },
                  },
                },
              ],
            },
            {
              id: generateId(),
              type: "button" as ElementType,
              className: "order-button",
              content: "ORDER NOW",
              styles: {
                width: "347px",
                height: "64px",
                borderRadius: "10px",
                backgroundImage:
                  "linear-gradient(180deg, #FFF4A3 0%, #FF8E07 100%)",
                border: "1px solid #FF900B",
                boxShadow:
                  "inset 0 0 7.81px 0 #FFF4A3, 0 4px 6px 0 rgba(176, 96, 0, 0.2)",
                color: "forground",
                fontWeight: 800,
                fontFamily: "Inter, sans-serif",
                fontSize: "28px",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 0,
                marginBottom: "0",
                marginTop: "10px",
                cursor: "pointer",
                boxSizing: "border-box",
                letterSpacing: "0.5px",
              },
              typography: "Button",
              responsiveStyles: {
                tablet: { width: "100%", fontSize: "22px", height: "56px" },
                mobile: { width: "100%", fontSize: "20px", height: "48px" },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "final-tagline",
              content: "Sharper Mind. Stronger Gums. Complete Confidence.",
              styles: {
                fontSize: "19px",
                fontWeight: "300",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                margin: "0px 10px",
              },
              responsiveStyles: {
                tablet: { fontSize: "18px" },
                mobile: { fontSize: "16px" },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Ingredients Section ======================
const createIngredientsSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "ingredients-showcase-section",
  styles: {
    backgroundColor: "#f5f5f5",
    padding: "0px 20px 20px",
    minHeight: "auto",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
    justifyItems: "center",
    fontFamily: "Poppins, sans-serif",
  },
  children: [
    // Section Header
    {
      id: generateId(),
      type: "heading" as ElementType,
      className: "ingredients-section-header",
      content:
        'Why <span style="color: #00bfff;">INTELLIDENT PRO™</span> Works When Nothing Else Does',
      styles: {
        fontSize: "3rem",
        lineHeight: "1.2",
        textAlign: "center",
        fontFamily: 'SFProDisplay-Bold", sans-serif',
        fontWeight: "700",
        margin: "40px 0 40px 0",
        color: "forground",
        maxWidth: "800px",
      },
      responsiveStyles: {
        tablet: { fontSize: "2.5rem" },
        mobile: { fontSize: "2rem" },
      },
    },
    {
      id: generateId(),
      type: "paragraph" as ElementType,
      content:
        "Most oral health products only mask the problem - <b>INTELLIDENT PRO™ targets the root cause.</b><br/>This advanced formula blends probiotics, prebiotics, and natural ingredients to <b>boost mental clarity, enhance focus, support gums and freshen breath</b> in seconds a day.",
      styles: {
        fontSize: "1.35rem",
        color: "#222",
        textAlign: "center",
        fontFamily: "pro-display-regular, sans-serif",
        fontWeight: "400",
        margin: "0 auto 32px auto",
        maxWidth: "1080px",
        lineHeight: "1.5",
      },
      responsiveStyles: {
        tablet: { fontSize: "1.5rem" },
        mobile: { fontSize: "1.2rem" },
      },
    },

    // Container Wrapper
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "ingredients-container-wrapper",
      styles: {
        maxWidth: "1272px",
        margin: "0 auto",
        position: "relative",
        padding: "0 20px",
        alignItems: "center",
        fontFamily: "Poppins, sans-serif",
      },
      children: [
        // Ingredients Grid
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "ingredients-grid-layout",
          styles: {
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: "40px 60px",
            marginBottom: "50px",
            alignItems: "center",
            justifyContent: "center",
            justifyItems: "center",
            fontFamily: "Poppins, sans-serif",
          },
          responsiveStyles: {
            mobile: {
              gridTemplateColumns: "1fr",
              gap: "25px",
              marginBottom: "30px",
            },
            tablet: {
              gridTemplateColumns: "1fr 1fr",
              gap: "30px",
              marginBottom: "40px",
            },
          },
          children: [
            // Ingredient 1
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "individual-ingredient-card",
              styles: {
                display: "flex",
                alignItems: "flex-start",
                gap: "20px",
              },
              responsiveStyles: {
                mobile: { gap: "12px" },
                tablet: { gap: "15px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-visual-content",
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset14.webp",
                      alt: "Bifidobacterium Lactis",
                      className: "ingredient-circular-image",
                      styles: {
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      responsiveStyles: {
                        tablet: { width: "80px", height: "80px" },
                        mobile: { width: "70px", height: "70px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-text-content",
                  styles: {
                    flex: 1,
                    paddingTop: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    maxWidth: "280px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      className: "ingredient-name-title",
                      content: "Bifidobacterium Lactis",
                      styles: {
                        fontSize: "22px",
                        marginBottom: "8px",
                        lineHeight: "1.3",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        color: "black",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "19px", marginBottom: "6px" },
                        mobile: { fontSize: "18px", marginBottom: "5px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "ingredient-benefit-description",
                      content:
                        'Strengthens the <span class="font-pro-display-bold">gut-brain axis</span> for <span class="font-pro-display-bold">sharper focus and mental clarity</span>.',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "1.4",
                        fontFamily: "pro-display-regular, sans-serif",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "17px", lineHeight: "1.3" },
                        mobile: { fontSize: "16px", lineHeight: "1.3" },
                      },
                    },
                  ],
                },
              ],
            },
            // Ingredient 2
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "individual-ingredient-card",
              styles: {
                display: "flex",
                alignItems: "flex-start",
                gap: "20px",
              },
              responsiveStyles: {
                mobile: { gap: "12px" },
                tablet: { gap: "15px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-visual-content",
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset15.webp",
                      alt: "Lactobacillus Paracasei",
                      className: "ingredient-circular-image",
                      styles: {
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      responsiveStyles: {
                        tablet: { width: "80px", height: "80px" },
                        mobile: { width: "70px", height: "70px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-text-content",
                  styles: {
                    flex: 1,
                    paddingTop: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    maxWidth: "280px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      className: "ingredient-name-title",
                      content: "Lactobacillus Paracasei",
                      styles: {
                        fontSize: "22px",
                        marginBottom: "8px",
                        lineHeight: "1.3",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        color: "black",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "19px", marginBottom: "6px" },
                        mobile: { fontSize: "18px", marginBottom: "5px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "ingredient-benefit-description",
                      content:
                        'Helps regulate <span class="font-pro-display-bold">brain and oral health</span>, supporting cognition and stronger gums.',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "1.4",
                        fontFamily: "pro-display-regular, sans-serif",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "17px", lineHeight: "1.3" },
                        mobile: { fontSize: "16px", lineHeight: "1.3" },
                      },
                    },
                  ],
                },
              ],
            },
            // Ingredient 3
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "individual-ingredient-card",
              styles: {
                display: "flex",
                alignItems: "flex-start",
                gap: "20px",
              },
              responsiveStyles: {
                mobile: { gap: "12px" },
                tablet: { gap: "15px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-visual-content",
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset16.webp",
                      alt: "Lactobacillus Reuteri",
                      className: "ingredient-circular-image",
                      styles: {
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      responsiveStyles: {
                        tablet: { width: "80px", height: "80px" },
                        mobile: { width: "70px", height: "70px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-text-content",
                  styles: {
                    flex: 1,
                    paddingTop: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    maxWidth: "280px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      className: "ingredient-name-title",
                      content: "Lactobacillus Reuteri",
                      styles: {
                        fontSize: "22px",
                        marginBottom: "8px",
                        lineHeight: "1.3",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        color: "black",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "19px", marginBottom: "6px" },
                        mobile: { fontSize: "18px", marginBottom: "5px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "ingredient-benefit-description",
                      content:
                        'Reduces <span class="font-pro-display-bold">inflammation linked to cognitive decline</span> and supports a balanced mind.',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "1.4",
                        fontFamily: "pro-display-regular, sans-serif",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "17px", lineHeight: "1.3" },
                        mobile: { fontSize: "16px", lineHeight: "1.3" },
                      },
                    },
                  ],
                },
              ],
            },
            // Ingredient 4
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "individual-ingredient-card",
              styles: {
                display: "flex",
                alignItems: "flex-start",
                gap: "20px",
              },
              responsiveStyles: {
                mobile: { gap: "12px" },
                tablet: { gap: "15px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-visual-content",
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset17.webp",
                      alt: "Inulin (Prebiotic)",
                      className: "ingredient-circular-image",
                      styles: {
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      responsiveStyles: {
                        tablet: { width: "80px", height: "80px" },
                        mobile: { width: "70px", height: "70px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-text-content",
                  styles: {
                    flex: 1,
                    paddingTop: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    maxWidth: "280px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      className: "ingredient-name-title",
                      content: "Inulin (Prebiotic)",
                      styles: {
                        fontSize: "22px",
                        marginBottom: "8px",
                        lineHeight: "1.3",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        color: "black",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "19px", marginBottom: "6px" },
                        mobile: { fontSize: "18px", marginBottom: "5px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "ingredient-benefit-description",
                      content:
                        'Fuels beneficial bacteria to <span class="font-pro-display-bold">enhance memory and learning</span>.',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "1.4",
                        fontFamily: "pro-display-regular, sans-serif",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "17px", lineHeight: "1.3" },
                        mobile: { fontSize: "16px", lineHeight: "1.3" },
                      },
                    },
                  ],
                },
              ],
            },
            // Ingredient 5
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "individual-ingredient-card",
              styles: {
                display: "flex",
                alignItems: "flex-start",
                gap: "20px",
              },
              responsiveStyles: {
                mobile: { gap: "12px" },
                tablet: { gap: "15px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-visual-content",
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset18.webp",
                      alt: "Lactobacillus Salivarius",
                      className: "ingredient-circular-image",
                      styles: {
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      responsiveStyles: {
                        tablet: { width: "80px", height: "80px" },
                        mobile: { width: "70px", height: "70px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-text-content",
                  styles: {
                    flex: 1,
                    paddingTop: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    maxWidth: "280px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      className: "ingredient-name-title",
                      content: "Lactobacillus Salivarius",
                      styles: {
                        fontSize: "22px",
                        marginBottom: "8px",
                        lineHeight: "1.3",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        color: "black",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "19px", marginBottom: "6px" },
                        mobile: { fontSize: "18px", marginBottom: "5px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "ingredient-benefit-description",
                      content:
                        'Promotes <span class="font-pro-display-bold">a healthy mouth</span> while helping to reduce <span class="font-pro-display-bold">brain fog</span>.',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "1.4",
                        fontFamily: "pro-display-regular, sans-serif",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "17px", lineHeight: "1.3" },
                        mobile: { fontSize: "16px", lineHeight: "1.3" },
                      },
                    },
                  ],
                },
              ],
            },
            // Ingredient 6
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "individual-ingredient-card",
              styles: {
                display: "flex",
                alignItems: "flex-start",
                gap: "20px",
              },
              responsiveStyles: {
                mobile: { gap: "12px" },
                tablet: { gap: "15px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-visual-content",
                  styles: {},
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset19.webp",
                      alt: "Peppermint Oil Powder",
                      className: "ingredient-circular-image",
                      styles: {
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                      },
                      responsiveStyles: {
                        tablet: { width: "80px", height: "80px" },
                        mobile: { width: "70px", height: "70px" },
                      },
                    },
                  ],
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "ingredient-text-content",
                  styles: {
                    flex: 1,
                    paddingTop: "5px",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    maxWidth: "280px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      className: "ingredient-name-title",
                      content: "Peppermint Oil Powder",
                      styles: {
                        fontSize: "22px",
                        marginBottom: "8px",
                        lineHeight: "1.3",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        color: "black",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "19px", marginBottom: "6px" },
                        mobile: { fontSize: "18px", marginBottom: "5px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      className: "ingredient-benefit-description",
                      content:
                        'Naturally freshens breath and supports <span class="font-pro-display-bold">mental alertness</span>.',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "1.4",
                        fontFamily: "pro-display-regular, sans-serif",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "17px", lineHeight: "1.3" },
                        mobile: { fontSize: "16px", lineHeight: "1.3" },
                      },
                    },
                  ],
                },
              ],
            },
          ],
        },
        // Product Summary Section
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "product-summary-section",
          styles: {
            textAlign: "center",
            margin: "40px 0px",
            padding: "0 20px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
          responsiveStyles: {
            tablet: { margin: "30px 0", padding: "0 12px" },
            mobile: { margin: "25px 0", padding: "0 10px" },
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "intellident-effectiveness-statement",
              content:
                "By improving brain function and oral health together, Intellident Pro™ helps you stay sharp, confident, and refreshed - every day.",
              styles: {
                fontSize: "26px",
                lineHeight: "1.5",
                marginBottom: "35px",
                maxWidth: "860px",
                marginLeft: "auto",
                marginRight: "auto",
                fontFamily: "pro-display-regular, sans-serif",
              },
              responsiveStyles: {
                tablet: {
                  fontSize: "22px",
                  marginBottom: "25px",
                  maxWidth: "600px",
                  lineHeight: "1.4",
                },
                mobile: {
                  fontSize: "20px",
                  marginBottom: "20px",
                  maxWidth: "100%",
                  lineHeight: "1.35",
                },
              },
            },
            {
              id: generateId(),
              type: "button" as ElementType,
              className: "primary-order-cta-button",
              content: "ORDER NOW",
              styles: {
                width: "347px",
                height: "64px",
                borderRadius: "10px",
                backgroundImage:
                  "linear-gradient(180deg, #FFF4A3 0%, #FF8E07 100%)",
                border: "1px solid secondary",
                boxShadow: "inset 0 0 7.81px 0 #FFF4A3",
                color: "forground",
                fontWeight: 800,
                fontFamily: "Inter, sans-serif",
                fontSize: "28px",
                textTransform: "uppercase",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: 0,
                marginBottom: "30px",
                marginTop: "10px",
                cursor: "pointer",
                boxSizing: "border-box",
                letterSpacing: "0.5px",
              },
              typography: "Button",
              responsiveStyles: {
                tablet: { width: "100%", fontSize: "22px", height: "56px" },
                mobile: { width: "100%", fontSize: "20px", height: "48px" },
              },
              onClick: { scrollToId: "packages", behavior: "smooth" },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "transformation-tagline-text",
              content:
                'Sharpen Your Mind. Strengthen Your Smile. <span class="font-pro-display-regular-italic italic">Start Your Transformation Today!</span>',
              styles: {
                fontSize: "26px",
                fontFamily: "pro-display-regular, sans-serif",
                margin: 0,
              },
              responsiveStyles: {
                tablet: { fontSize: "22px", lineHeight: "1.3" },
                mobile: {
                  fontSize: "20px",
                  lineHeight: "1.3",
                  padding: "0 5px",
                },
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Product Showcase Section ======================
const createProductShowcaseSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "product-showcase-main-section",
  styles: {
    backgroundImage: "linear-gradient(to right, #87ceeb, #1e9dc8)",
    padding: "40px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    width: "100%",
  },
  responsiveStyles: {
    tablet: {
      padding: "30px 15px",
    },
    mobile: {
      padding: "20px 10px",
      overflow: "hidden",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "showcase-main-container",
      styles: {
        maxWidth: "1272px",
        width: "100%",
        display: "grid",
        gridTemplateColumns: "40% 60%",
        gap: "40px",
        alignItems: "center",
        position: "relative",
        zIndex: "2",
        boxSizing: "border-box",
      },
      responsiveStyles: {
        tablet: {
          gridTemplateColumns: "1fr",
          gap: "30px",
          maxWidth: "100%",
          width: "100%",
          padding: "0 15px",
          overflow: "hidden",
          boxSizing: "border-box",
          margin: "0 auto",
        },
        mobile: {
          gridTemplateColumns: "1fr",
          gap: "20px",
          maxWidth: "100%",
          width: "100%",
          padding: "0 10px",
          overflow: "hidden",
          boxSizing: "border-box",
          margin: "0 auto",
        },
      },
      children: [
        // Left Side - Product Image
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "image-section",
          styles: {
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            width: "100%",
            minWidth: "0",
            boxSizing: "border-box",
          },
          responsiveStyles: {
            tablet: {
              width: "100%",
              overflow: "hidden",
              maxWidth: "100%",
            },
            mobile: {
              width: "100%",
              overflow: "hidden",
              maxWidth: "100%",
              padding: "0 10px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "bottle-main-container",
              styles: {
                position: "relative",
                zIndex: "5",
                marginLeft: "-2rem",
              },
              responsiveStyles: {
                tablet: {
                  marginLeft: "0",
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                },
                mobile: {
                  display: "flex",
                  justifyContent: "center",
                  width: "100%",
                  margin: "0 auto",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/newbottle2.webp",
                  alt: "Intellident Pro Advanced Oral Formula Bottle",
                  className: "b-image",
                  styles: {
                    width: "100%",
                    maxWidth: "100%",
                    height: "auto",
                    objectFit: "contain",
                    transition: "transform 0.3s",
                  },
                  responsiveStyles: {
                    tablet: {
                      width: "100%",
                      maxWidth: "400px",
                      maxHeight: "500px",
                    },
                    mobile: {
                      width: "100%",
                      maxWidth: "300px",
                      maxHeight: "400px",
                    },
                  },
                },
              ],
            },
          ],
        },
        // Right Side - Text Content
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "content-right-side",
          styles: {
            maxWidth: "620px",
            width: "100%",
            minWidth: "0",
            padding: "0 20px",
            position: "relative",
            zIndex: 2,
            boxSizing: "border-box",
          },
          responsiveStyles: {
            tablet: {
              marginLeft: "0",
              maxWidth: "100%",
              width: "100%",
              padding: "0 15px",
              boxSizing: "border-box",
              minWidth: "0",
            },
            mobile: {
              margin: "0 auto",
              padding: "0 10px",
              maxWidth: "100%",
              width: "100%",
              boxSizing: "border-box",
              minWidth: "0",
            },
          },
          children: [
            // Main Headline
            {
              id: generateId(),
              type: "heading" as ElementType,
              className: "primary-main-headline",
              content:
                '<span class="headline-white-text">A Smarter Way to Boost Your Oral & Brain Health - </span><span style="color:#051168;font-family:pro-display-bold-italic, sans-serif;font-style:italic;">Naturally!</span>',
              styles: {
                fontSize: "53px",
                fontWeight: "700",
                lineHeight: "1.2",
                letterSpacing: "-0.01em",
                marginBottom: "16px",
                color: "background",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
              },
              typography: "Heading-2",
              responsiveStyles: {
                tablet: {
                  fontSize: "28px",
                  marginBottom: "14px",
                  textAlign: "center",
                },
                mobile: { fontSize: "24px", textAlign: "center" },
              },
            },
            // Subheading
            {
              id: generateId(),
              type: "heading" as ElementType,
              className: "primary-subheading",
              content: "Sharpens Focus & Strengthens Gums",
              styles: {
                fontSize: "18px",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                fontWeight: "700",
                color: "background",
                marginBottom: "20px",
                opacity: "0.95",
              },
              responsiveStyles: {
                tablet: { textAlign: "center" },
                mobile: { textAlign: "center" },
              },
            },
            // First Description Paragraph
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "content-description-paragraph",
              content:
                "Powered by clinically studied probiotics and prebiotics, Intellident Pro™ works at the root level to enhance mental clarity and support oral health - all in just seconds a day.",
              styles: {
                fontSize: "18px",
                fontWeight: "300",
                lineHeight: "1.5",
                color: "background",
                marginBottom: "18px",
                opacity: "0.92",
              },
              typography: "Body",
              responsiveStyles: {
                tablet: { textAlign: "center" },
                mobile: { textAlign: "center" },
              },
            },
            // Science Section Title
            {
              id: generateId(),
              type: "heading" as ElementType,
              className: "science-section-heading",
              content: "Backed by Science, Designed for Results",
              styles: {
                fontSize: "18px",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                fontWeight: "700",
                color: "background",
                marginBottom: "16px",
                marginTop: "8px",
              },
              responsiveStyles: {
                tablet: { textAlign: "center" },
                mobile: { textAlign: "center" },
              },
            },
            // Second Description Paragraph
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "content-description-paragraph",
              content:
                "Formulated using cutting-edge research, this once-daily chewable helps you stay sharp, confident, and fresh - without relying on mouthwashes, caffeine, or complicated routines.",
              styles: {
                fontSize: "18px",
                fontWeight: "300",
                lineHeight: "1.5",
                color: "background",
                marginBottom: "18px",
                opacity: "0.92",
              },
              typography: "Body",
              responsiveStyles: {
                tablet: { textAlign: "center" },
                mobile: { textAlign: "center" },
              },
            },
            // Order Now Button
            {
              id: generateId(),
              type: "button" as ElementType,
              className: "main-order-button",
              content: "ORDER NOW",
              styles: {
                backgroundImage:
                  "linear-gradient(180deg, #FFF4A3 0%, #FF8E07 100%)",
                width: "347px",
                height: "64px",
                color: "forground",
                fontSize: "28px",
                fontWeight: "bold",
                border: "1px solid rgba(241, 210, 68, 0.92)",
                borderRadius: "8px",
                cursor: "pointer",
                margin: "24px 0 20px 0",
                boxShadow: "inset 0 0 7.81px 0 #FFF4A3",
                textTransform: "uppercase",
                letterSpacing: "1px",
                transition: "all 0.3s ease",
                display: "inline-block",
              },
              responsiveStyles: {
                tablet: {
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block",
                },
                mobile: {
                  marginLeft: "auto",
                  marginRight: "auto",
                  display: "block",
                  width: "100%",
                },
              },
              onClick: { scrollToId: "packages", behavior: "smooth" },
            },
            // Bottom Tagline
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              className: "final-tagline-text",
              content:
                'Think Sharper. Smile Brighter. <span class="font-pro-display-regular-italic italic">Feel the Difference TODAY!</span>',
              styles: {
                fontSize: "20px",
                fontWeight: "400",
                color: "background",
                opacity: "0.95",
                marginTop: "12px",
              },
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Customer Review Section ======================
const createCustomerReviewSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  className: "customer-review-section-wrapper",
  type: "container" as ElementType,
  styles: {
    backgroundColor: "#f5f5f5",
    width: "100%",
    padding: "0",
  },
  responsiveStyles: {
    tablet: { paddingLeft: "8px", paddingRight: "8px" },
    mobile: { paddingLeft: "4px", paddingRight: "4px", paddingTop: "1rem" },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "customer-review-section",
      styles: {
        maxWidth: "1200px",
        margin: "0 auto",
        textAlign: "center",
        paddingTop: "2rem",
        paddingBottom: "1rem",
        paddingLeft: "12px",
        paddingRight: "12px",
      },
      responsiveStyles: {
        tablet: { paddingLeft: "8px", paddingRight: "8px" },
        mobile: { paddingLeft: "4px", paddingRight: "4px", paddingTop: "1rem" },
      },
      children: [
        // Headline and 5-star badge (new design)
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "review-header-block",
          styles: {
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "18px",
            marginBottom: "32px",
          },
          responsiveStyles: {
            tablet: { gap: "14px", marginBottom: "24px" },
            mobile: { gap: "12px", marginBottom: "20px" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "center",
                margin: "0 0 8px 0",
              },
              children: [
                // Large "5" number
                {
                  id: generateId(),
                  type: "text" as ElementType,
                  content: "5",
                  styles: {
                    fontSize: "132.95px",
                    fontWeight: "900",
                    // fontFamily: "SF Pro Display",
                    color: "#F9AD24",
                    lineHeight: "90%",
                    margin: "0",
                    letterSpacing: "0px",
                    fontStyle: "italic",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "100px" },
                    mobile: { fontSize: "80px" },
                  },
                },
                // "STAR-RATING" container with text and stars
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                  },
                  responsiveStyles: {
                    tablet: { gap: "8px" },
                    mobile: { gap: "6px" },
                  },
                  children: [
                    // "STAR-RATING" text
                    {
                      id: generateId(),
                      type: "text" as ElementType,
                      content: "STAR-RATING",
                      styles: {
                        fontSize: "39.36px",
                        fontWeight: "900",
                        fontFamily: "Inter",
                        color: "forground",
                        textTransform: "uppercase",
                        letterSpacing: "0px",
                        margin: "0",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "32px", lineHeight: "8px" },
                        mobile: { fontSize: "24px", lineHeight: "6px" },
                      },
                    },
                    // Stars under the text with white background
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        justifyContent: "flex-start",
                        gap: "8px",
                        margin: "0",
                        backgroundColor: "background",
                        padding: "8px 16px",
                        borderRadius: "20px",
                      },
                      responsiveStyles: {
                        tablet: {
                          gap: "6px",
                          padding: "6px 12px",
                          borderRadius: "16px",
                        },
                        mobile: {
                          gap: "4px",
                          padding: "4px 8px",
                          borderRadius: "12px",
                        },
                      },
                      children: Array(5)
                        .fill(0)
                        .map(() => ({
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/intellident/group-15.webp",
                          alt: "star",
                          styles: {
                            width: "43px",
                            height: "40px",
                            objectFit: "contain",
                          },
                          responsiveStyles: {
                            tablet: { width: "28px", height: "28px" },
                            mobile: { width: "24px", height: "24px" },
                          },
                        })),
                    },
                  ],
                },
              ],
            },
            {
              id: generateId(),
              type: "heading" as ElementType,
              content: "<b>Countless Five-Star Reviews</b> and Counting!",
              styles: {
                fontSize: "20px",
                color: "#222",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                fontWeight: "400",
                margin: "18px 0 0 0",
              },
              responsiveStyles: {
                tablet: { fontSize: "1.8rem" },
                mobile: { fontSize: "1.4rem" },
              },
            },
            {
              id: generateId(),
              type: "heading" as ElementType,
              content:
                'Many People Are Experiencing the <span style="color:#00bfff;">INTELLIDENT PRO™</span> Difference',
              styles: {
                fontSize: "48px",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                fontWeight: "700",
                margin: "18px 0 0 0",
                lineHeight: "1.1",
                maxWidth: "900px",
              },
              responsiveStyles: {
                tablet: { fontSize: "2.4rem", maxWidth: "100%" },
                mobile: { fontSize: "1.8rem", maxWidth: "100%" },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content:
                "Real people. Real results. A healthier mind and mouth - starting in just days.",
              styles: {
                fontSize: "21px",
                color: "#222",
                fontFamily: "pro-display-regular, sans-serif",
                margin: "10px 0 0 0",
                fontWeight: "300",
              },
              responsiveStyles: {
                tablet: { fontSize: "1.1rem" },
                mobile: { fontSize: "1rem" },
              },
            },
          ],
        },
        // Polaroid Photo (single image)
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "client-photo-container",
          styles: {
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "2.5rem 0 2.5rem 0",
          },
          responsiveStyles: {
            tablet: { margin: "2rem 0 2rem 0" },
            mobile: { margin: "1.5rem 0 1.5rem 0" },
          },
          children: [
            {
              id: generateId(),
              type: "image" as ElementType,
              src: "/intellident/client-photo.png",
              alt: "Happy customers holding INTELLIDENT PRO",
              styles: {
                width: "100%",
                maxWidth: "900px",
                height: "auto",
                filter: "drop-shadow(0 0 5px rgba(0,0,0,0.18))",
              },
              responsiveStyles: {
                tablet: { maxWidth: "100%" },
                mobile: { maxWidth: "100%" },
              },
            },
          ],
        },
        // Testimonial Responsive Section
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            width: "100%",
            maxWidth: "1272px",
            margin: "0 auto",
            padding: "0 24px",
            fontFamily: "pro-display-light, sans-serif",
          },
          responsiveStyles: {
            tablet: { padding: "0 16px" },
            mobile: { padding: "0 8px" },
          },
          children: [
            // Testimonial 1 - Leah T.
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "row",
                gap: "15px",
                marginBottom: "1.5rem",
              },
              responsiveStyles: {
                tablet: {
                  flexDirection: "column",
                  gap: "15px",
                },
                mobile: {
                  flexDirection: "column",
                  gap: "15px",
                },
              },
              children: [
                // Profile Photo
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    flexShrink: 0,
                    width: "101px",
                    height: "98px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "3px solid background",
                  },
                  responsiveStyles: {
                    tablet: { width: "96px", height: "96px" },
                    mobile: { width: "80px", height: "80px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset27.webp",
                      alt: "Leah T. profile photo",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      },
                    },
                  ],
                },
                // Testimonial Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: { flex: 1, textAlign: "left" },
                  responsiveStyles: {
                    tablet: { textAlign: "center", maxWidth: "100%" },
                    mobile: { textAlign: "center", maxWidth: "100%" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        '"I used to deal with brain fog and bad breath that made me feel embarrassed. Now, I can think clearly, stay focused, and feel good about myself every day!"',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "30px",
                        marginBottom: "14px",
                        fontWeight: "400",
                        color: "#414042",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "18px" },
                        mobile: { fontSize: "16px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "Leah T., 43",
                      styles: {
                        fontSize: "16px",
                        fontWeight: "700",
                        fontFamily: "pro-display-semibold, sans-serif",
                        color: "forground",
                        marginBottom: "14px",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "20px" },
                        mobile: { fontSize: "18px" },
                      },
                    },
                    // Stars and Premium Product
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      },
                      responsiveStyles: {
                        tablet: { justifyContent: "center" },
                        mobile: {
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: "0.3rem",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            gap: "0.2rem",
                            minHeight: "fit-content",
                          },
                          children: Array(5)
                            .fill(0)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/star.svg",
                              alt: "5 star rating",
                              styles: { width: "24px", height: "24px" },
                              responsiveStyles: {
                                tablet: { width: "18px", height: "18px" },
                                mobile: { width: "16px", height: "16px" },
                              },
                            })),
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "Premium Product",
                          styles: {
                            fontSize: "18px",
                            fontWeight: "400",
                            color: "#414042",
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // Testimonial 2 - Mike S.
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "row",
                gap: "15px",
                marginBottom: "1.5rem",
              },
              responsiveStyles: {
                tablet: {
                  flexDirection: "column",
                  gap: "15px",
                },
                mobile: {
                  flexDirection: "column",
                  gap: "15px",
                },
              },
              children: [
                // Profile Photo
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    flexShrink: 0,
                    width: "101px",
                    height: "98px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "3px solid background",
                  },
                  responsiveStyles: {
                    tablet: { width: "96px", height: "96px" },
                    mobile: { width: "80px", height: "80px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset28.webp",
                      alt: "Mike S. profile photo",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      },
                    },
                  ],
                },
                // Testimonial Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: { flex: 1, textAlign: "left" },
                  responsiveStyles: {
                    tablet: { textAlign: "center", maxWidth: "100%" },
                    mobile: { textAlign: "center", maxWidth: "100%" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        '"I used to forget names all the time, which wasn\'t great for my work. Ever since I started using Intelligent Pro™, my memory is sharper, and my gums feel healthier too!"',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "30px",
                        marginBottom: "14px",
                        fontWeight: "400",
                        color: "#414042",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "18px" },
                        mobile: { fontSize: "16px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "Mike S., 48",
                      styles: {
                        fontSize: "16px",
                        fontWeight: "700",
                        fontFamily: "pro-display-semibold, sans-serif",
                        color: "forground",
                        marginBottom: "14px",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "20px" },
                        mobile: { fontSize: "18px" },
                      },
                    },
                    // Stars and Premium Product
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      },
                      responsiveStyles: {
                        tablet: { justifyContent: "center" },
                        mobile: {
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: "0.3rem",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            gap: "0.2rem",
                            minHeight: "fit-content",
                          },
                          children: Array(5)
                            .fill(0)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/star.svg",
                              alt: "5 star rating",
                              styles: { width: "24px", height: "24px" },
                              responsiveStyles: {
                                tablet: { width: "18px", height: "18px" },
                                mobile: { width: "16px", height: "16px" },
                              },
                            })),
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "Premium Product",
                          styles: {
                            fontSize: "18px",
                            fontWeight: "400",
                            color: "#414042",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "16px" },
                            mobile: { fontSize: "14px" },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // Testimonial 3 - Mary J.
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "row",
                gap: "15px",
                marginBottom: "1.5rem",
              },
              responsiveStyles: {
                tablet: {
                  flexDirection: "column",
                  gap: "15px",
                },
                mobile: {
                  flexDirection: "column",
                  gap: "15px",
                },
              },
              children: [
                // Profile Photo
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    flexShrink: 0,
                    width: "101px",
                    height: "98px",
                    borderRadius: "50%",
                    overflow: "hidden",
                    border: "3px solid background",
                  },
                  responsiveStyles: {
                    tablet: { width: "96px", height: "96px" },
                    mobile: { width: "80px", height: "80px" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset29.webp",
                      alt: "Mary J. profile photo",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                      },
                    },
                  ],
                },
                // Testimonial Content
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: { flex: 1, textAlign: "left" },
                  responsiveStyles: {
                    tablet: { textAlign: "center", maxWidth: "100%" },
                    mobile: { textAlign: "center", maxWidth: "100%" },
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        '"I was scared of losing my memory like my dad. But after using Intelligent Pro™, I think more clearly, have more energy, and my breath stays fresh all day."',
                      styles: {
                        fontSize: "21px",
                        lineHeight: "30px",
                        marginBottom: "14px",
                        fontWeight: "400",
                        color: "#414042",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "18px" },
                        mobile: { fontSize: "16px" },
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "Mary J., 36",
                      styles: {
                        fontSize: "16px",
                        fontWeight: "700",
                        fontFamily: "pro-display-semibold, sans-serif",
                        color: "forground",
                        marginBottom: "14px",
                      },
                      responsiveStyles: {
                        tablet: { fontSize: "20px" },
                        mobile: { fontSize: "18px" },
                      },
                    },
                    // Stars and Premium Product
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      },
                      responsiveStyles: {
                        tablet: { justifyContent: "center" },
                        mobile: {
                          justifyContent: "center",
                          flexDirection: "column",
                          gap: "0.3rem",
                        },
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            gap: "0.2rem",
                            minHeight: "fit-content",
                          },
                          children: Array(5)
                            .fill(0)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/star.svg",
                              alt: "5 star rating",
                              styles: { width: "24px", height: "24px" },
                              responsiveStyles: {
                                tablet: { width: "18px", height: "18px" },
                                mobile: { width: "16px", height: "16px" },
                              },
                            })),
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "Premium Product",
                          styles: {
                            fontSize: "18px",
                            fontWeight: "400",
                            color: "#414042",
                          },
                          responsiveStyles: {
                            tablet: { fontSize: "16px" },
                            mobile: { fontSize: "14px" },
                          },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Guarantee Section ======================
const createGuaranteeSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "guarantee-section",
  styles: { width: "100%" },
  children: [
    // Blue Section Three
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "linear-blue-gradient1",
      styles: {
        backgroundImage:
          "linear-gradient(to right, #65c6e7 30%, #37b4e1 60%, #1e9ac8 80%)",
        width: "100%",
      },
      padding: "0 1rem",
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            padding: "3.5rem 0",
            maxWidth: "1000px",
            margin: "0 auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            gap: "84px",
          },
          responsiveStyles: {
            tablet: {
              flexDirection: "row",
              gap: "1.5rem",
              padding: "2rem 1rem",
            },
            mobile: {
              padding: "2rem 1rem",
              gap: "1rem",
              flexDirection: "column",
            },
          },
          children: [
            // Golden Badge
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: { flexShrink: 0 },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/Asset-6.webp",
                  alt: "Golden Badge",
                  styles: { width: "280px", height: "auto" },
                  responsiveStyles: {
                    tablet: { width: "192px" },
                    mobile: { width: "128px" },
                  },
                },
              ],
            },
            // Text Content
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: { flex: 1 },
              responsiveStyles: {
                tablet: { textAlign: "left" },
                mobile: { textAlign: "center" },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "Limited Supply Available",
                  styles: {
                    fontSize: "53px",
                    fontFamily: 'SFProDisplay-Bold", sans-serif',
                    color: "background",
                    marginBottom: "1rem",
                    lineHeight: "100%",
                    fontWeight: "700",
                  },
                  typography: "Heading-2",
                  responsiveStyles: {
                    tablet: { fontSize: "2rem", marginBottom: "0.75rem" },
                    mobile: { fontSize: "1.5rem", marginBottom: "0.5rem" },
                  },
                },
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "Selling Out Fast!",
                  styles: {
                    fontSize: "53px",
                    fontFamily: "pro-display-semibold-italic, sans-serif",
                    marginBottom: "1.5rem",
                    color: "quaternary",
                    letterSpacing: "0.02em",
                    fontWeight: "700",
                    fontStyle: "italic",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "2rem", marginBottom: "1rem" },
                    mobile: { fontSize: "1.5rem", marginBottom: "0.75rem" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Due to high demand, INTELLIDENT PRO™ is selling out faster than expected. Once this batch is gone, restocking could take months.",
                  styles: {
                    fontSize: "21px",
                    color: "background",
                    lineHeight: "26px",
                    fontFamily: "pro-display-light, sans-serif",
                    letterSpacing: "0.02em",
                    marginBottom: "1rem",
                    fontWeight: "300",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "1.5rem", marginBottom: "0.75rem" },
                    mobile: { fontSize: "1rem", marginBottom: "0.5rem" },
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content: "Order now while supplies last!",
                  styles: {
                    fontSize: "23px",
                    color: "background",
                    lineHeight: "31px",
                    fontFamily: "pro-display-bold-italic, sans-serif",
                    letterSpacing: "0.02em",
                    fontWeight: "700",
                    fontStyle: "italic",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "1.5rem" },
                    mobile: { fontSize: "1rem" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    // Light Gray Content Section
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        backgroundImage:
          "linear-gradient(180deg, #f0f0f033 0%, #66666633 100%)",
        paddingTop: "3rem",
        paddingLeft: "1rem",
        paddingRight: "1rem",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { paddingTop: "2.5rem" },
        mobile: { paddingTop: "2rem" },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            margin: "0 auto",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          },
          children: [
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content: "Claim Your DISCOUNTED INTELLIDENT PRO™",
              styles: {
                maxWidth: "1200px",
                margin: "0 auto",
                fontSize: "48px",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                marginBottom: "0.75rem",
                lineHeight: "1",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem",
                fontWeight: "700",
                color: "#0B0B0B",
              },
              typography: "Heading-3",
              responsiveStyles: {
                tablet: { fontSize: "2rem", marginBottom: "0.5rem" },
                mobile: { fontSize: "20px", marginBottom: "0.5rem" },
              },
            },
            {
              id: generateId(),
              type: "paragraph" as ElementType,
              content: "Below While Stock Last!",
              styles: {
                maxWidth: "1200px",
                margin: "0 auto",
                fontSize: "48px",
                fontFamily: 'SFProDisplay-Bold", sans-serif',
                marginBottom: "2rem",
                lineHeight: "1",
                paddingLeft: "0.5rem",
                paddingRight: "0.5rem",
                fontWeight: "700",
                color: "#0B0B0B",
              },
              typography: "Heading-3",
              responsiveStyles: {
                tablet: { fontSize: "2rem", marginBottom: "1.5rem" },
                mobile: { fontSize: "18pxrem", marginBottom: "1rem" },
              },
            },
          ],
        },
      ],
    },
    // Bottom section with triangle pointer
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: { position: "relative", backgroundColor: "#f5f5f5" },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: { backgroundColor: "#f5f5f5", height: "64px" },
        },
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            position: "absolute",
            width: 0,
            height: 0,
            left: "50%",
            top: 0,
            marginLeft: "-16px",
            borderLeft: "30px solid transparent",
            borderTop: "30px solid #D6D6D6",
            borderRight: "30px solid transparent",
          },
        },
      ],
    },
  ],
});

// ====================== Pricing Plan Section ======================
const createPricingPlanSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "pricing-plan-section",
  styles: {
    backgroundColor: "#f5f5f5",
    padding: "0px 20px 20px",
    minHeight: "auto",
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    flexDirection: "column",
    justifyItems: "center",
    fontFamily: "Poppins, sans-serif",
    width: "100%",
    boxSizing: "border-box",
  },
  responsiveStyles: {
    tablet: {
      padding: "0px 15px 20px",
      overflow: "hidden",
    },
    mobile: {
      padding: "0px 10px 20px",
      overflow: "hidden",
    },
  },
  children: [
    {
      id: "packages",
      type: "container" as ElementType,
      className: "pricing-plan-section",
      styles: {
        maxWidth: "1200px",
        width: "100%",
        margin: "0 auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "1.5rem",
        padding: "1.5rem",
        boxSizing: "border-box",
      },
      responsiveStyles: {
        tablet: {
          padding: "2rem 1.5rem",
          maxWidth: "100%",
          overflow: "hidden",
        },
        mobile: {
          padding: "1rem",
          maxWidth: "100%",
          overflow: "hidden",
          boxSizing: "border-box",
        },
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          styles: {
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            gap: "1rem",
            width: "100%",
            boxSizing: "border-box",
          },
          responsiveStyles: {
            tablet: {
              flexDirection: "column",
              gap: "1.5rem",
              padding: "2rem 1rem",
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            },
            mobile: {
              padding: "1rem 0.5rem",
              gap: "1rem",
              flexDirection: "column",
              width: "100%",
              maxWidth: "100%",
              overflow: "hidden",
              boxSizing: "border-box",
            },
          },
          children: [
            // Card 1: 1 Month Package
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                backgroundColor: "background",
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                width: "100%",
                maxWidth: "400px",
                minWidth: "280px",
                overflow: "hidden",
                border: "1px solid #d1d5db",
                transition: "all 0.3s",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                boxSizing: "border-box",
              },
              responsiveStyles: {
                tablet: {
                  maxWidth: "100%",
                  width: "100%",
                  minWidth: "0",
                  boxSizing: "border-box",
                },
                mobile: {
                  maxWidth: "100%",
                  width: "100%",
                  minWidth: "0",
                  boxSizing: "border-box",
                  margin: "0 auto",
                },
              },
              children: [
                // Stars
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: "4px",
                    marginTop: "7px",
                    marginBottom: "8px",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/star.svg",
                      alt: "star",
                      styles: {
                        width: "28px",
                        height: "28px",
                        color: "#FFB800",
                      },
                    },
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/star.svg",
                      alt: "star",
                      styles: {
                        width: "28px",
                        height: "28px",
                        color: "#FFB800",
                      },
                    },
                  ],
                },
                // Title
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: { textAlign: "center", marginBottom: "8px" },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "STARTER PACK",
                      styles: {
                        fontSize: "21px",
                        fontWeight: "400",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        letterSpacing: "1px",
                        marginBottom: "2px",
                        color: "#231F20",
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "1 Month Package",
                      styles: {
                        fontSize: "27px",
                        fontWeight: "700",
                        fontFamily: "pro-display-semibold, sans-serif",
                        color: "#231F20",
                      },
                    },
                  ],
                },
                // Product Image + Badge
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    position: "relative",
                    minHeight: "180px",
                    margin: "18px 0 8px 0",
                    backgroundImage:
                      "radial-gradient(circle, #ffffff, #d1d5db)",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        position: "absolute",
                        top: "-29px",
                        left: "0",
                        width: "100%",
                        height: "50px",
                        backgroundColor: "background",
                        zIndex: 1,
                        borderRadius: "50%",
                      },
                    },
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset30.webp",
                      alt: "INTELLIDENT PRO Bottle",
                      styles: {
                        width: "100%",
                        objectFit: "contain",
                        zIndex: 2,
                      },
                    },
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset36.webp",
                      alt: "Guarantee Badge",
                      styles: {
                        width: "78px",
                        height: "78px",
                        position: "absolute",
                        right: "50px",
                        bottom: "50px",
                        zIndex: 3,
                      },
                    },
                  ],
                },
                // Price
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: { textAlign: "center", margin: "8px 0 1rem 0" },
                  children: [
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "<span style='font-size: 53px;'>$</span>69",
                      styles: {
                        color: "#bcbec0",
                        fontSize: "80px",
                        fontWeight: "900",
                        fontFamily: "SFProDisplay-Bold, sans-serif",
                        lineHeight: "100%",
                      },
                    },
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content: "per bottle",
                      styles: {
                        fontSize: "14px",
                        lineHeight: "1",
                        color: "#BCBEC0",
                        marginTop: "-4px",
                        fontWeight: "300",
                      },
                    },
                  ],
                },
                // Buy Now Button
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    padding: "2rem",
                    borderTop: "1px solid #d1d5db",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        backgroundImage:
                          "linear-gradient(180deg, #8FE0FF 0%, #26A1D0 100%)",
                        color: "forground",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        fontSize: "24px",
                        padding: "12px 32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        textDecoration: "none",
                        margin: "0 auto",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        width: "100%",
                        textAlign: "center",
                      },
                      onClick: {
                        navigate:
                          "process.php?action=initial_purchase&bottles=1",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content: "BUY NOW",
                          styles: {
                            fontWeight: "900",
                            fontSize: "26px",
                            color: "background",
                            width: "100%",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Security Badges
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    textAlign: "center",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        position: "relative",
                        display: "inline-block",
                        width: "320px",
                        height: "auto",
                        marginLeft: "2rem",
                        marginRight: "2rem",
                      },
                      children: [
                        // Background border
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            width: "100%",
                            border: "1px solid #ccc",
                            borderRadius: "30px",
                            backgroundColor: "background",
                            position: "relative",
                          },
                        },
                        // Text positioned on the border
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            top: "-5px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "background",
                            padding: "0 16px",
                            zIndex: 2,
                            minHeight: "fit-content",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "GUARANTEED SAFE CHECKOUT",
                              styles: {
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#58595B",
                                margin: 0,
                                lineHeight: "1",
                                letterSpacing: "0.5px",
                                whiteSpace: "nowrap",
                                background: "transparent",
                              },
                            },
                          ],
                        },
                        // Payment badges container
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "calc(100% - 40px)",
                            padding: "0 20px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset7.webp",
                              alt: "McAfee",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/clip-path-group-4.webp",
                              alt: "PayPal",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/kamqdp-1.webp",
                              alt: "Visa",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset 7.webp",
                              alt: "American Express",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/rectangle-1.webp",
                              alt: "MasterCard",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                // Total
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    textAlign: "center",
                    fontFamily: "pro-display-medium, sans-serif",
                    margin: "8px 0 18px 0",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        'TOTAL: <span style="text-decoration:line-through;">$89</span> <span style="color:#000000;font-family:pro-display-heavy;margin-left:4px;">$69</span>',
                      styles: { fontSize: "16px" },
                    },
                  ],
                },
              ],
            },
            // Card 2: 6 Month Package (Featured) - Container with Banner
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                position: "relative",
                width: "100%",
                maxWidth: "400px",
                minWidth: "280px",
                margin: "3rem 0 1.5rem 0",
                boxSizing: "border-box",
              },
              responsiveStyles: {
                tablet: {
                  maxWidth: "100%",
                  width: "100%",
                  minWidth: "0",
                  margin: "3rem 0 1.5rem 0",
                  boxSizing: "border-box",
                },
                mobile: {
                  maxWidth: "100%",
                  width: "100%",
                  minWidth: "0",
                  margin: "2rem 0 1rem 0",
                  boxSizing: "border-box",
                },
              },
              children: [
                // Hovering Best Value Banner
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    position: "absolute",
                    top: "-40px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 10,
                    width: "280px",
                    height: "60px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/best-value-banner.png",
                      alt: "Best Value Banner",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                        position: "absolute",
                      },
                    },
                    {
                      id: generateId(),
                      type: "heading" as ElementType,
                      content: "BEST VALUE",
                      styles: {
                        color: "background",
                        fontWeight: "700",
                        fontSize: "27px",
                        textAlign: "center",
                        zIndex: 2,
                        position: "relative",
                        margin: "0",
                        letterSpacing: "1px",
                      },
                    },
                  ],
                },
                // Main Card
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    backgroundColor: "#F3FFE6",
                    borderRadius: "16px",
                    boxShadow: "0 20px 25px rgba(0,0,0,0.15)",
                    width: "100%",
                    overflow: "hidden",
                    border: "2px solid #006838",
                    transform: "scale(1.05)",
                    transition: "all 0.3s",
                  },
                  children: [
                    // Light Green Section: Stars + Title
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        backgroundColor: "#f0fdf4",
                        padding: "1rem 1rem 0.75rem 1rem",
                        marginTop: "20px",
                      },
                      children: [
                        // Stars
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            justifyContent: "center",
                            minHeight: "fit-content",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              styles: {
                                display: "flex",
                                gap: "0.25rem",
                                minHeight: "fit-content",
                              },
                              children: Array(5)
                                .fill(0)
                                .map(() => ({
                                  id: generateId(),
                                  type: "image" as ElementType,
                                  src: "/intellident/star.svg",
                                  alt: "5 star rating",
                                  styles: { width: "28px", height: "28px" },
                                })),
                            },
                          ],
                        },
                        // Package Title
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: { textAlign: "center" },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "MOST POPULAR",
                              styles: {
                                fontSize: "20px",
                                fontWeight: "400",
                                marginBottom: "0.5rem",
                                fontFamily: 'SFProDisplay-Bold", sans-serif',
                              },
                            },
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "6 Month Package",
                              styles: {
                                fontSize: "29px",
                                fontWeight: "700",
                                fontFamily: "pro-display-semibold, sans-serif",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Free Shipping Banner
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      className: "container-cover4",
                      styles: {
                        padding: "0.5rem 0",
                        backgroundImage:
                          "linear-gradient(259.57deg, #007616 0.36%, #003F09 114.52%)",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "1rem",
                            minHeight: "fit-content",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset 9.webp",
                              alt: "Free Shipping Banner",
                              styles: { marginRight: "8px", width: "50px" },
                            },
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "FREE US SHIPPING",
                              styles: {
                                color: "background",
                                fontFamily: 'SFProDisplay-Bold", sans-serif',
                                fontSize: "22px",
                                fontWeight: "700",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Blue Section with Products and Curve
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      className: "container-cover2",
                      styles: {
                        height: "auto",
                        position: "relative",
                        overflow: "hidden",
                        padding: "0 0 1rem 0px",
                        backgroundImage:
                          "radial-gradient(circle, #ffffff, #008ccc)",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            height: "auto",
                            position: "relative",
                            overflow: "hidden",
                            paddingBottom: "1rem",
                          },
                          children: [
                            // Product Images Section
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              styles: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                minHeight: "160px",
                                position: "relative",
                              },
                              children: [
                                // Floating Free Ebooks Badge
                                {
                                  id: generateId(),
                                  type: "container" as ElementType,
                                  styles: {
                                    position: "absolute",
                                    top: "10px",
                                    right: "20px",
                                    zIndex: 50,
                                    backgroundColor: "#DCF4FA",
                                    borderRadius: "25px",
                                    padding: "8px 16px",
                                    minHeight: "fit-content",
                                  },
                                  children: [
                                    {
                                      id: generateId(),
                                      type: "container" as ElementType,
                                      styles: {
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "6px",
                                        minHeight: "fit-content",
                                      },
                                      children: [
                                        {
                                          id: generateId(),
                                          type: "image" as ElementType,
                                          src: "/intellident/check.png",
                                          alt: "Check icon",
                                          styles: {
                                            width: "16px",
                                            height: "16px",
                                            objectFit: "contain",
                                          },
                                        },
                                        {
                                          id: generateId(),
                                          type: "heading" as ElementType,
                                          content: "2 FREE EBOOKS!",
                                          styles: {
                                            fontFamily:
                                              'SFProDisplay-Bold", sans-serif',
                                            fontSize: "11px",
                                            fontWeight: "700",
                                            color: "#231F20",
                                            margin: "0",
                                          },
                                        },
                                      ],
                                    },
                                  ],
                                },
                                // Multiple Bottles
                                {
                                  id: generateId(),
                                  type: "container" as ElementType,
                                  styles: {
                                    position: "relative",
                                    display: "flex",
                                    flexWrap: "wrap",
                                    justifyContent: "center",
                                    alignItems: "center",
                                  },
                                  children: [
                                    {
                                      id: generateId(),
                                      type: "image" as ElementType,
                                      src: "/intellident/Asset35.webp",
                                      alt: "INTELLIDENT PRO Bottle",
                                      styles: {
                                        width: "100%",
                                        height: "auto",
                                        objectFit: "contain",
                                      },
                                    },
                                  ],
                                },
                              ],
                            },
                          ],
                        },
                        // U-shaped curve section
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "relative",
                            height: "32px",
                            zIndex: 20,
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              className: "container-cover3",
                              styles: {
                                position: "absolute",
                                bottom: "0",
                                left: 0,
                                width: "100%",
                                height: "32px",
                                borderRadius: "50% 50% 0 0",
                                backgroundImage:
                                  "radial-gradient(circle, #e6f7ff, #b3e7ff)",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Light Blue Section: Pricing
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      className: "container-cover3",
                      styles: {
                        padding: "0 1rem 1rem 1rem",
                        position: "relative",
                        height: "150px",
                        marginTop: "-32px",
                        backgroundImage:
                          "radial-gradient(circle, #e6f7ff, #b3e7ff)",
                      },
                      children: [
                        // Pricing
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            textAlign: "center",
                            marginBottom: "0.75rem",
                            position: "relative",
                            zIndex: 30,
                            transform: "translateY(16px)",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              styles: {
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "center",
                                marginBottom: 0,
                                fontFamily: "pro-display-heavy, sans-serif",
                              },
                              children: [
                                {
                                  id: generateId(),
                                  type: "heading" as ElementType,
                                  content: "$",
                                  styles: {
                                    color: "#007616",
                                    fontSize: "58px",
                                    transform: "translateY(0px)",
                                    fontWeight: "900",
                                    lineHeight: "100%",
                                  },
                                },
                                {
                                  id: generateId(),
                                  type: "heading" as ElementType,
                                  content: "49",
                                  styles: {
                                    color: "#007616",
                                    fontSize: "87px",
                                    fontWeight: "900",
                                    lineHeight: "100%",
                                  },
                                },
                              ],
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "per bottle",
                              styles: {
                                color: "#231F20",
                                fontSize: "15px",
                                marginTop: "-12px",
                                marginLeft: "30px",
                                fontWeight: "400",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Buy Now Button
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                        padding: "2rem",
                        borderTop: "1px solid #d1d5db",
                        position: "relative",
                        zIndex: "100",
                        marginTop: "-36x",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            backgroundImage:
                              "linear-gradient(0deg, #FF8E07, #FF8E07), linear-gradient(180deg, #FFF4A3 0%, #fff4a300 85%)",
                            color: "forground",
                            fontFamily: 'SFProDisplay-Bold", sans-serif',
                            fontWeight: "700",
                            fontSize: "24px",
                            padding: "12px 32px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "0.5rem",
                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                            textDecoration: "none",
                            margin: "0 auto",
                            borderRadius: "8px",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            width: "100%",
                            textAlign: "center",
                            border: "1px solid #FF900B",
                          },
                          onClick: {
                            navigate:
                              "process.php?action=initial_purchase&bottles=1",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "BUY NOW",
                              styles: {
                                fontWeight: "900",
                                fontSize: "26px",
                                color: "forground",
                                width: "100%",
                              },
                            },
                          ],
                        },
                      ],
                    },
                    // Security Badges
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        textAlign: "center",
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "relative",
                            display: "inline-block",
                            width: "320px",
                            height: "auto",
                            marginLeft: "2rem",
                            marginRight: "2rem",
                          },
                          children: [
                            // Background border
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              styles: {
                                width: "100%",
                                border: "1px solid #ccc",
                                borderRadius: "30px",
                                position: "relative",
                              },
                            },
                            // Text positioned on the border
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              styles: {
                                position: "absolute",
                                top: "-5px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                backgroundColor: "#F3FFE6",
                                padding: "0 16px",
                                zIndex: 2,
                                minHeight: "fit-content",
                              },
                              children: [
                                {
                                  id: generateId(),
                                  type: "paragraph" as ElementType,
                                  content: "GUARANTEED SAFE CHECKOUT",
                                  styles: {
                                    fontSize: "11px",
                                    fontWeight: "500",
                                    color: "#58595B",
                                    margin: 0,
                                    lineHeight: "1",
                                    letterSpacing: "0.5px",
                                    whiteSpace: "nowrap",
                                    background: "transparent",
                                  },
                                },
                              ],
                            },
                            // Payment badges container
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              styles: {
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: "calc(100% - 40px)",
                                padding: "0 20px",
                              },
                              children: [
                                {
                                  id: generateId(),
                                  type: "image" as ElementType,
                                  src: "/intellident/Asset7.webp",
                                  alt: "McAfee",
                                  styles: {
                                    height: "16px",
                                    objectFit: "contain",
                                  },
                                },
                                {
                                  id: generateId(),
                                  type: "image" as ElementType,
                                  src: "/intellident/clip-path-group-4.webp",
                                  alt: "PayPal",
                                  styles: {
                                    height: "16px",
                                    objectFit: "contain",
                                  },
                                },
                                {
                                  id: generateId(),
                                  type: "image" as ElementType,
                                  src: "/intellident/kamqdp-1.webp",
                                  alt: "Visa",
                                  styles: {
                                    height: "16px",
                                    objectFit: "contain",
                                  },
                                },
                                {
                                  id: generateId(),
                                  type: "image" as ElementType,
                                  src: "/intellident/Asset 7.webp",
                                  alt: "American Express",
                                  styles: {
                                    height: "16px",
                                    objectFit: "contain",
                                  },
                                },
                                {
                                  id: generateId(),
                                  type: "image" as ElementType,
                                  src: "/intellident/rectangle-1.webp",
                                  alt: "MasterCard",
                                  styles: {
                                    height: "16px",
                                    objectFit: "contain",
                                  },
                                },
                              ],
                            },
                          ],
                        },
                      ],
                    },
                    // Total
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        textAlign: "center",
                        fontFamily: "pro-display-medium, sans-serif",
                        margin: "8px 0 18px 0",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content:
                            'TOTAL: <span style="text-decoration:line-through;">$1,010</span> <span style="color:#000000;font-family:pro-display-heavy;margin-left:4px;">$294</span>',
                          styles: { fontSize: "16px" },
                        },
                      ],
                    },
                  ],
                },
              ],
            },
            // Card 3: 3 Month Package
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                backgroundColor: "background",
                borderRadius: "16px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
                width: "100%",
                maxWidth: "400px",
                minWidth: "280px",
                overflow: "hidden",
                border: "1px solid #d1d5db",
                transition: "all 0.3s",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                boxSizing: "border-box",
              },
              responsiveStyles: {
                tablet: {
                  maxWidth: "100%",
                  width: "100%",
                  minWidth: "0",
                  boxSizing: "border-box",
                },
                mobile: {
                  maxWidth: "100%",
                  width: "100%",
                  minWidth: "0",
                  boxSizing: "border-box",
                  margin: "0 auto",
                },
              },
              children: [
                // Top White Section: Stars + Title
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    backgroundColor: "background",
                    padding: "1rem 1rem 0.75rem 1rem",
                  },
                  children: [
                    // Stars
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        justifyContent: "center",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: { display: "flex", gap: "0.25rem" },
                          children: Array(3)
                            .fill(0)
                            .map(() => ({
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/star.svg",
                              alt: "5 star rating",
                              styles: { width: "28px", height: "28px" },
                            })),
                        },
                      ],
                    },
                    // Package Title
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: { textAlign: "center" },
                      children: [
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "BEST OFFER",
                          styles: {
                            fontSize: "20px",
                            fontWeight: "400",
                            marginBottom: "0.5rem",
                            fontFamily: 'SFProDisplay-Bold", sans-serif',
                            color: "#231F20",
                          },
                        },
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content: "3 Month Package",
                          styles: {
                            fontSize: "26px",
                            fontWeight: "700",
                            fontFamily: "pro-display-semibold, sans-serif",
                            color: "#231F20",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Free Shipping Banner
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    backgroundImage:
                      "linear-gradient(267.09deg, #F1F2F2 26.33%, #E6E7E8 96.23%)",
                    padding: "0.5rem 0",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                        minHeight: "auto",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "image" as ElementType,
                          src: "/intellident/Asset11.webp",
                          alt: "Free Shipping Banner",
                          styles: {
                            color: "forground",
                            marginRight: "8px",
                            width: "56px",
                          },
                        },
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content: "FREE US SHIPPING",
                          styles: {
                            fontFamily: '"SFProDisplay-Bold", sans-serif',
                            fontSize: "23px",
                            fontWeight: "700",
                            color: "#231F20",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Gray Section with Products
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "container-cover",
                  styles: {
                    padding: "1rem 1rem 1.5rem 1rem",
                    backgroundImage:
                      "radial-gradient(circle, #ffffff, #d1d5db)",
                  },
                  children: [
                    // Product Images Section
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: "160px",
                        position: "relative",
                      },
                      children: [
                        // Product Bottles and Book
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          },
                          children: [
                            // Three Bottles
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset34.webp",
                              alt: "INTELLIDENT PRO Bottle",
                              styles: {
                                width: "100%",
                                maxWidth: "300px",
                                height: "auto",
                                objectFit: "contain",
                                filter:
                                  "drop-shadow(0 10px 8px rgba(0,0,0,0.3))",
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                // Light Pink Section: Pricing
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    padding: "1rem 1rem 1rem 1rem",
                    borderBottom: "1px solid #d1d5db",
                  },
                  children: [
                    // Pricing
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: { textAlign: "center", alignItems: "center" },
                      children: [
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "center",
                            marginBottom: "0.5rem",
                            fontFamily: "pro-display-heavy, sans-serif",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "$",
                              styles: {
                                fontSize: "53px",
                                transform: "translateY(0px)",
                                fontWeight: "900",
                                fontFamily: "SFProDisplay-Bold, sans-serif",
                                lineHeight: "100%",
                                color: "#231F20",
                              },
                            },
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "59",
                              styles: {
                                fontSize: "80px",
                                fontWeight: "900",
                                fontFamily: "SFProDisplay-Bold, sans-serif",
                                lineHeight: "100%",
                                color: "#231F20",
                              },
                            },
                          ],
                        },
                        {
                          id: generateId(),
                          type: "paragraph" as ElementType,
                          content: "per bottle",
                          styles: {
                            fontSize: "14px",
                            marginLeft: "50px",
                            marginTop: "-12px",
                            color: "#BCBEC0",
                            fontWeight: "400",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Buy Now Button
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                    padding: "2rem",
                    borderTop: "1px solid #d1d5db",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        backgroundImage:
                          "linear-gradient(180deg, #8FE0FF 0%, #26A1D0 100%)",
                        color: "forground",
                        fontFamily: 'SFProDisplay-Bold", sans-serif',
                        fontWeight: "700",
                        fontSize: "24px",
                        padding: "12px 32px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "0.5rem",
                        boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                        textDecoration: "none",
                        margin: "0 auto",
                        borderRadius: "8px",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        width: "100%",
                        textAlign: "center",
                      },
                      onClick: {
                        navigate:
                          "process.php?action=initial_purchase&bottles=1",
                      },
                      children: [
                        {
                          id: generateId(),
                          type: "heading" as ElementType,
                          content: "BUY NOW",
                          styles: {
                            fontWeight: "900",
                            fontSize: "26px",
                            color: "background",
                            width: "100%",
                          },
                        },
                      ],
                    },
                  ],
                },
                // Security Badges
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    textAlign: "center",
                    width: "100%",
                    display: "flex",
                    justifyContent: "center",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      styles: {
                        position: "relative",
                        display: "inline-block",
                        width: "320px",
                        height: "auto",
                        marginLeft: "2rem",
                        marginRight: "2rem",
                      },
                      children: [
                        // Background border
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            width: "100%",
                            border: "1px solid #ccc",
                            borderRadius: "30px",
                            backgroundColor: "background",
                            position: "relative",
                          },
                        },
                        // Text positioned on the border
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            top: "-5px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "background",
                            padding: "0 16px",
                            zIndex: 2,
                            minHeight: "fit-content",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "GUARANTEED SAFE CHECKOUT",
                              styles: {
                                fontSize: "11px",
                                fontWeight: "500",
                                color: "#58595B",
                                margin: 0,
                                lineHeight: "1",
                                letterSpacing: "0.5px",
                                whiteSpace: "nowrap",
                                background: "transparent",
                              },
                            },
                          ],
                        },
                        // Payment badges container
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          styles: {
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "calc(100% - 40px)",
                            padding: "0 20px",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset7.webp",
                              alt: "McAfee",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/clip-path-group-4.webp",
                              alt: "PayPal",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/kamqdp-1.webp",
                              alt: "Visa",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset 7.webp",
                              alt: "American Express",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/rectangle-1.webp",
                              alt: "MasterCard",
                              styles: {
                                height: "16px",
                                objectFit: "contain",
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
                // Total
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    textAlign: "center",
                    fontFamily: "pro-display-medium, sans-serif",
                    margin: "8px 0 18px 0",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "paragraph" as ElementType,
                      content:
                        'TOTAL: <span style="text-decoration:line-through;">$349</span> <span style="color:#000000;font-family:pro-display-heavy;margin-left:4px;">$177</span>',
                      styles: { fontSize: "16px" },
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== New Certificate Section ======================
const createNewCertificateSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "certificate-section",
  styles: {
    width: "100%",
    height: "859px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
  },
  responsiveStyles: {
    tablet: { height: "600px" },
    mobile: { height: "400px" },
  },
  children: [
    // Background Image
    {
      id: generateId(),
      type: "image" as ElementType,
      src: "/intellident/certificate-background.jpg",
      alt: "Certificate Background",
      styles: {
        position: "absolute",
        top: "0",
        left: "0",
        width: "100%",
        height: "859px",
        objectFit: "cover",
        zIndex: "1",
      },
      responsiveStyles: {
        tablet: { height: "600px" },
        mobile: { height: "400px" },
      },
    },
    // Certificate Image
    {
      id: generateId(),
      type: "image" as ElementType,
      src: "/intellident/certificate.jpg",
      alt: "Certificate",
      styles: {
        position: "relative",
        width: "456px",
        height: "auto",
        zIndex: "2",
        maxWidth: "90%",
      },
      responsiveStyles: {
        tablet: { width: "350px" },
        mobile: { width: "280px" },
      },
    },
  ],
});

// ====================== Original Certificate Section ======================
const createCertificateSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "guarantee-section",
  styles: {
    backgroundImage:
      "linear-gradient(135deg, #00bfff 0%, #1e90ff 30%, #4169e1 70%, #0000cd 100%)",
    minHeight: "100vh",
    padding: "50px 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  responsiveStyles: {
    tablet: { padding: "30px 15px" },
    mobile: { padding: "30px 15px" },
  },
  children: [
    // Diagonal line pattern overlay
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage:
          "repeating-linear-gradient(45deg, transparent, transparent 1px, rgba(255, 255, 255, 0.05) 1px, rgba(255, 255, 255, 0.05) 2px), repeating-linear-gradient(-45deg, transparent, transparent 1px, rgba(255, 255, 255, 0.05) 1px, rgba(255, 255, 255, 0.05) 2px)",
        zIndex: "1",
      },
    },
    // Container
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "guarantee-container",
      styles: {
        maxWidth: "800px",
        width: "100%",
        position: "relative",
        zIndex: "2",
      },
      children: [
        // Certificate Frame - Dark Border (OUTERMOST)
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "certificate-frame",
          styles: {
            backgroundColor: "#064b84",
            padding: "15px",
            boxShadow: "0 15px 35px rgba(0, 0, 0, 0.4)",
          },
          responsiveStyles: {
            tablet: { padding: "12px" },
            mobile: { padding: "10px" },
          },
          children: [
            // White Frame (MIDDLE LAYER)
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "white-frame",
              styles: {
                backgroundColor: "background",
                padding: "15px",
                position: "relative",
              },
              responsiveStyles: {
                tablet: { padding: "12px" },
                mobile: { padding: "10px" },
              },
              children: [
                // Blue Decorative Border Frame
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  className: "blue-border",
                  styles: {
                    backgroundImage: "linear-gradient(45deg, #5dade2, #3498db)",
                    padding: "15px",
                    position: "relative",
                    border: "4px solid forground",
                  },
                  responsiveStyles: {
                    tablet: { padding: "10px" },
                    mobile: { padding: "8px" },
                  },
                  children: [
                    // Content Card with THICK BLACK BORDER
                    {
                      id: generateId(),
                      type: "container" as ElementType,
                      className: "content-card",
                      styles: {
                        padding: "35px 30px",
                        border: "4px solid forground",
                        position: "relative",
                        zIndex: "2",
                        textAlign: "center",
                        backgroundImage:
                          "radial-gradient(circle at 25% 25%, rgba(0, 0, 0, 0.02) 1px, transparent 1px), radial-gradient(circle at 75% 75%, rgba(0, 0, 0, 0.02) 1px, transparent 1px)",
                        backgroundSize: "25px 25px",
                        backgroundColor: "background",
                      },
                      responsiveStyles: {
                        tablet: { padding: "25px 20px", borderWidth: "12px" },
                        mobile: { padding: "20px 15px", borderWidth: "10px" },
                      },
                      children: [
                        // Header
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          className: "header",
                          styles: { marginBottom: "25px" },
                          children: [
                            {
                              id: generateId(),
                              type: "container" as ElementType,
                              className: "logo-row",
                              styles: {
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: "8px",
                              },
                              children: [
                                {
                                  id: generateId(),
                                  type: "container" as ElementType,
                                  className: "logo-icon",
                                  styles: {},
                                  children: [
                                    {
                                      id: generateId(),
                                      type: "image" as ElementType,
                                      src: "/intellident/intellidentlogo.png",
                                      alt: "logo",
                                      styles: { width: "100%", height: "auto" },
                                    },
                                  ],
                                },
                                {
                                  id: generateId(),
                                  type: "span" as ElementType,
                                  content: "INTELLIDENT PRO™",
                                  className: "logo-text",
                                  styles: {
                                    fontSize: "16px",
                                    fontWeight: "bold",
                                    color: "#2c3e50",
                                    letterSpacing: "1px",
                                  },
                                },
                              ],
                            },
                          ],
                        },
                        // Title Section
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          className: "title-section",
                          styles: { marginBottom: "25px" },
                          children: [
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "60-DAY IRONCLAD",
                              className: "ironclad-title",
                              styles: {
                                fontSize: "46px",
                                fontWeight: "bold",
                                fontFamily: 'SFProDisplay-Bold", sans-serif',
                                color: "#3498db",
                                letterSpacing: "-0.02em",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "30px",
                                  letterSpacing: "1px",
                                },
                                mobile: { fontSize: "28px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "heading" as ElementType,
                              content: "GUARANTEE",
                              className: "guarantee-title",
                              styles: {
                                fontSize: "64px",
                                fontWeight: "bold",
                                fontFamily: 'SFProDisplay-Bold", sans-serif',
                                color: "#3498db",
                                letterSpacing: "3px",
                                marginBottom: "12px",
                                lineHeight: "1",
                              },
                              responsiveStyles: {
                                tablet: {
                                  fontSize: "46px",
                                  letterSpacing: "2px",
                                },
                                mobile: {
                                  fontSize: "38px",
                                  letterSpacing: "1px",
                                },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "Love It or Get Your Money Back!",
                              className: "main-subtitle",
                              styles: {
                                fontSize: "40px",
                                fontFamily: 'SFProDisplay-Bold", sans-serif',
                                fontWeight: "600",
                                color: "forground",
                                margin: "0",
                                lineHeight: "1.5",
                                letterSpacing: "0px",
                              },
                              responsiveStyles: {
                                tablet: { fontSize: "28px" },
                                mobile: { fontSize: "26px" },
                              },
                            },
                          ],
                        },
                        // Content Text
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          className: "content-text",
                          styles: {
                            marginBottom: "24px",
                            lineHeight: "1.5",
                            fontFamily: "pro-display-regular, sans-serif",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "You're Covered by Our 60-Day, 100% Money-Back Guarantee",
                              className: "coverage-line",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "18px",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "We believe so strongly in what Intellident Pro™ can do for your mind, your smile, and your daily confidence - that we're putting our money where our mouth is.",
                              className: "body-text",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "15px",
                                lineHeight: "1.2",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                'That\'s why every single order is backed by our <span style="font-family:pro-display-semibold;font-weight:600;">100% risk-free, 60-day money-back guarantee.</span>',
                              className: "condition-line",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "6px",
                                lineHeight: "1.4",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "If you don't feel the mental clarity...",
                              className: "condition-line",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "6px",
                                lineHeight: "1.4",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "If your smile doesn't feel fresher, stronger, and more confident...",
                              className: "body-text",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "15px",
                                lineHeight: "1.2",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "Or even if you simply change your mind for any reason at all.",
                              className: "body-text",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "15px",
                                lineHeight: "1.2",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "Just let us know within 60 days of your purchase, and we'll issue a full and friendly refund.",
                              className: "no-hassle",
                              styles: {
                                fontSize: "20px",
                                margin: "15px 0",
                                fontWeight: "600",
                                fontFamily: "pro-display-medium, sans-serif",
                                fontStyle: "italic",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "No hassles. No fine print. No awkward questions.",
                              className: "body-text",
                              styles: {
                                fontSize: "20px",
                                marginBottom: "15px",
                                lineHeight: "1.2",
                                fontFamily: "pro-display-regular, sans-serif",
                                fontStyle: "italic",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "We do this because:",
                              className: "reason-header",
                              styles: {
                                fontSize: "20px",
                                margin: "18px 0 8px 0",
                                fontWeight: "600",
                                fontFamily: "pro-display-semibold, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "We believe in the science behind this formula",
                              className: "reason-item",
                              styles: {
                                fontSize: "19px",
                                color: "#2c3e50",
                                marginBottom: "6px",
                                lineHeight: "1.4",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content: "We care deeply about your experience",
                              className: "reason-item",
                              styles: {
                                fontSize: "19px",
                                color: "#2c3e50",
                                marginBottom: "6px",
                                lineHeight: "1.4",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "And we're serious about helping you protect what matters most - your brain and your confidence",
                              className: "reason-item",
                              styles: {
                                fontSize: "19px",
                                color: "#2c3e50",
                                marginBottom: "6px",
                                lineHeight: "1.4",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                            {
                              id: generateId(),
                              type: "paragraph" as ElementType,
                              content:
                                "You'll find all the contact info you need (email + phone) on your confirmation page and inside every shipment - so reaching out is always easy.",
                              className: "contact-info",
                              styles: {
                                fontSize: "19px",
                                color: "#2c3e50",
                                marginTop: "18px",
                                lineHeight: "1.5",
                                fontFamily: "pro-display-regular, sans-serif",
                              },
                              responsiveStyles: {
                                mobile: { fontSize: "18px" },
                              },
                            },
                          ],
                        },
                        // Money Back Seal Image
                        {
                          id: generateId(),
                          type: "container" as ElementType,
                          className: "seal-container",
                          styles: {
                            display: "flex",
                            justifyContent: "center",
                            marginTop: "20px",
                            borderColor: "forground",
                          },
                          children: [
                            {
                              id: generateId(),
                              type: "image" as ElementType,
                              src: "/intellident/Asset40.webp",
                              alt: "60-Day Money Back Guarantee Seal",
                              className: "money-back-seal-image",
                              styles: {
                                width: "200px",
                                height: "200px",
                                display: "block",
                                borderRadius: "999px",
                              },
                              responsiveStyles: {
                                tablet: { width: "85px", height: "85px" },
                                mobile: { width: "75px", height: "75px" },
                              },
                            },
                          ],
                        },
                      ],
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

// ====================== Bonus Section ======================
const createBonusSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "bonus-section",
  styles: {
    padding: "40px 16px",
    maxWidth: "1200px",
    margin: "0 auto",
    width: "100%",
  },
  responsiveStyles: {
    tablet: { padding: "30px 16px" },
    mobile: { padding: "25px 16px" },
  },
  children: [
    // Header
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "bonus-header",
      styles: {
        textAlign: "center",
        marginBottom: "24px",
        maxWidth: "800px",
        marginLeft: "auto",
        marginRight: "auto",
      },
      children: [
        {
          id: generateId(),
          type: "heading" as ElementType,
          content:
            'Unlock Exclusive Bonuses -<br>Yours <span style="color:#000000;">FREE</span> With Your Order!',
          styles: {
            fontSize: "48px",
            fontFamily: 'SFProDisplay-Bold", sans-serif',
            fontWeight: "700",
            color: "#0B0B0B",
            marginBottom: "16px",
            lineHeight: "1.2",
          },
          typography: "Heading-3",
          responsiveStyles: {
            tablet: { fontSize: "40px" },
            mobile: { fontSize: "32px" },
          },
        },
        {
          id: generateId(),
          type: "paragraph" as ElementType,
          content:
            "Gain expert knowledge to boost brain power, support oral health, <br>and stay sharp for years to come - at no extra cost!",
          styles: {
            fontSize: "241x",
            color: "#414042",
            marginBottom: "32px",
            maxWidth: "400px",
            marginLeft: "auto",
            marginRight: "auto",
            lineHeight: "1.4",
          },
          responsiveStyles: {
            tablet: { fontSize: "20px" },
            mobile: { fontSize: "18px" },
          },
        },
      ],
    },
    // Red Banner
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "red-banner",
      styles: {
        display: "flex",
        justifyContent: "center",
        marginBottom: "32px",
        width: "100%",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "ribbon-container",
          styles: {
            display: "flex",
            alignItems: "center",
            position: "relative",
          },
          children: [
            // Optional: Left Tail
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "50px",
                height: "52px",
                display: "block",
                marginRight: "-25px",
                backgroundColor: "background",
                zIndex: "10",
                transform: "rotate(45deg)",
              },
              responsiveStyles: {
                mobile: { width: "28px", height: "28px", marginRight: "-13px" },
                tablet: { width: "34px", height: "37px", marginRight: "-17px" },
              },
            },
            // Main Ribbon
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "ribbon",
              styles: {
                backgroudColor: "#9b1c1c",
                backgroundImage:
                  "linear-gradient(239.99deg, #C70019 -141.1%, #C40018 -120.64%, #BA0218 -104.28%, #AA0418 -92.01%, #930817 -75.64%, #760D16 -63.37%, #5A1216 -55.19%, #C70019 38.91%, #C40018 67.55%, #BB0118 88%, #AB0418 104.37%, #960817 116.64%, #7A0C16 133%, #5A1216 145.28%, #C70019 268.01%)",
                padding: "18px 48px",
                color: "background",
                fontSize: "2rem",
                fontWeight: "900",
                fontFamily: "SFProDisplay-Bold, sans-serif",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                textAlign: "center",
                lineHeight: "1.1",
                zIndex: 2,
                minWidth: "320px",
                maxWidth: "100%",
                margin: "0",
                position: "relative",
              },
              responsiveStyles: {
                tablet: { fontSize: "1.5rem", padding: "14px 28px" },
                mobile: {
                  fontSize: "1.1rem",
                  padding: "10px 10px",
                  minWidth: "180px",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content: "GET 2 FREE BONUS BOOKS!!!",
                  styles: {
                    color: "background",
                    fontWeight: "700",
                    fontFamily: 'SFProDisplay-Bold", sans-serif',
                    fontSize: "34px",
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                  },
                  responsiveStyles: {
                    mobile: { fontSize: "13px" },
                    tablet: { fontSize: "20px" },
                  },
                },
              ],
            },
            // Optional: Right Tail
            {
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                width: "50px",
                height: "52px",
                display: "block",
                marginLeft: "-25px",
                backgroundColor: "background",
                zIndex: "10",
                transform: "rotate(45deg)",
              },
              responsiveStyles: {
                mobile: { width: "28px", height: "28px", marginLeft: "-13px" },
                tablet: { width: "34px", height: "37px", marginLeft: "-17px" },
              },
            },
          ],
        },
      ],
    },
    // Bonus Books Grid
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "bonus-books-grid",
      styles: {
        display: "grid",
        gridTemplateColumns: "repeat(2, 1fr)",
        gap: "40px",
        maxWidth: "800px",
        margin: "0 auto 32px auto",
      },
      responsiveStyles: {
        tablet: { gap: "20px", marginBottom: "28px" },
        mobile: {
          gridTemplateColumns: "1fr",
          gap: "16px",
          marginBottom: "24px",
          maxWidth: "350px",
        },
      },
      children: [
        // Bonus Book 1
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "bonus-book-1",
          styles: {
            backgroundColor: "background",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #d1d5db",
            maxWidth: "100%",
            margin: "0 auto",
            width: "100%",
          },
          responsiveStyles: {
            mobile: { maxWidth: "350px" },
          },
          children: [
            // Book Cover
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "book-cover",
              styles: {
                position: "relative",
                width: "100%",
                height: "400px",
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/Asset21.webp",
                  alt: "Think Young Again",
                  styles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "all 0.3s ease-in-out",
                  },
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: "96px",
                    height: "96px",
                    transform: "translate(0, 0)",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset23.webp",
                      alt: "Overlay Image",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      },
                    },
                  ],
                },
              ],
            },
            // Book Info
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "book-info",
              styles: {
                backgroundImage:
                  "radial-gradient(71.4% 382.07% at 85.29% 105.09%, #65CEE9 0%, #3FAED2 31%, #3399CC 87%)",
                color: "background",
                padding: "12px 8px",
                textAlign: "center",
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content:
                    "Bonus #1 - Think Young Again:<br>How Healthy Gums Lead To<br>Razor-Sharp Memory",
                  styles: {
                    fontSize: "19px",
                    fontFamily: "pro-display-medium, sans-serif",
                    fontWeight: "600",
                    lineHeight: "1.3",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: { fontSize: "16px" },
                  },
                },
              ],
            },
            // Price Section
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "price-section",
              styles: {
                backgroundImage:
                  "linear-gradient(180deg, #009444 0%, #39B54A 100%)",
                color: "background",
                padding: "8px",
                textAlign: "center",
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    '<span style="text-decoration:line-through;opacity:0.9;font-family:pro-display-medium,sans-serif;font-weight:800;">$67</span> <span style="color:#f9ed32;">Today: <span style="color:#ffffff;font-family:pro-display-bold,sans-serif;font-weight:800;">FREE</span></span>',
                  styles: {
                    fontSize: "21px",
                    fontFamily: "pro-display-medium, sans-serif",
                    fontWeight: "500",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: { fontSize: "16px" },
                  },
                },
              ],
            },
            // Description
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "book-description",
              styles: {
                padding: "16px",
                textAlign: "center",
                backgroundColor: "#f3f4f6",
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    'Discover the shocking connection<br>between <span style="font-family:pro-display-semi-bold,sans-serif;font-weight:700;">oral health and memory</span>,<br>backed by real stories and research.<br>Learn <span style="font-family:pro-display-semi-bold,sans-serif;font-weight:700;">simple, science-backed<br>steps</span> to keep your brain sharp and<br>gums healthy for life.',
                  styles: {
                    color: "#414042",
                    fontSize: "18px",
                    lineHeight: "1.6",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "14px" },
                    mobile: { fontSize: "12px" },
                  },
                },
              ],
            },
          ],
        },
        // Bonus Book 2
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "bonus-book-2",
          styles: {
            backgroundColor: "background",
            borderRadius: "24px",
            overflow: "hidden",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
            border: "1px solid #d1d5db",
            maxWidth: "100%",
            margin: "0 auto",
            width: "100%",
          },
          responsiveStyles: {
            mobile: { maxWidth: "350px" },
          },
          children: [
            // Book Cover
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "book-cover",
              styles: {
                position: "relative",
                width: "100%",
                height: "400px",
              },
              children: [
                {
                  id: generateId(),
                  type: "image" as ElementType,
                  src: "/intellident/Asset22.webp",
                  alt: "Brain Reboot",
                  styles: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "all 0.3s ease-in-out",
                  },
                },
                {
                  id: generateId(),
                  type: "container" as ElementType,
                  styles: {
                    position: "absolute",
                    bottom: 0,
                    right: 0,
                    width: "96px",
                    height: "96px",
                    transform: "translate(0, 0)",
                  },
                  children: [
                    {
                      id: generateId(),
                      type: "image" as ElementType,
                      src: "/intellident/Asset23.webp",
                      alt: "Overlay Image",
                      styles: {
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      },
                    },
                  ],
                },
              ],
            },
            // Book Info
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "book-info",
              styles: {
                backgroundImage:
                  "radial-gradient(71.4% 382.07% at 85.29% 105.09%, #65CEE9 0%, #3FAED2 31%, #3399CC 87%)",
                color: "background",
                padding: "12px 8px",
                textAlign: "center",
              },
              children: [
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  content:
                    "Bonus #2 - Brain Reboot:<br>30-Day Guide With Ancient<br>Asian Secrets",
                  styles: {
                    fontSize: "19px",
                    fontFamily: "pro-display-medium, sans-serif",
                    fontWeight: "600",
                    lineHeight: "1.3",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: { fontSize: "16px" },
                  },
                },
              ],
            },
            // Price Section
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "price-section",
              styles: {
                backgroundImage:
                  "linear-gradient(180deg, #009444 0%, #39B54A 100%)",
                color: "background",
                padding: "8px",
                textAlign: "center",
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    '<span style="text-decoration:line-through;opacity:0.9;font-family:pro-display-medium,sans-serif;font-weight:800;">$197</span> <span style="color:#f9ed32;">Today: <span style="color:#ffffff;font-family:pro-display-bold,sans-serif;font-weight:800;">FREE</span></span>',
                  styles: {
                    fontSize: "21px",
                    fontFamily: "pro-display-medium, sans-serif",
                    fontWeight: "500",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "18px" },
                    mobile: { fontSize: "16px" },
                  },
                },
              ],
            },
            // Description
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "book-description",
              styles: {
                padding: "16px",
                textAlign: "center",
                backgroundColor: "#f3f4f6",
                height: "100%",
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    'Unlock <span style="font-family:pro-display-semi-bold,sans-serif;font-weight:700;">ancient techniques and<br>modern breakthroughs for mental<br>clarity, better focus, and long-term<br>cognitive health</span> - all in an<br>easy-to-follow <span style="font-family:pro-display-semi-bold,sans-serif;font-weight:700;">30-day plan</span>.',
                  styles: {
                    color: "#414042",
                    fontSize: "18px",
                    lineHeight: "1.6",
                    margin: 0,
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "14px" },
                    mobile: { fontSize: "12px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    // Top Order Button
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "order-button-container",
      styles: {
        textAlign: "center",
        marginBottom: "24px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      children: [
        {
          id: generateId(),
          type: "button" as ElementType,
          className: "pack-btn",
          content: "ORDER NOW",
          styles: {
            width: "347px",
            height: "64px",
            borderRadius: "10px",
            backgroundImage:
              "linear-gradient(180deg, #FFF4A3 0%, #FF8E07 100%)",
            border: "1px solid secondary",
            boxShadow: "inset 0 0 7.81px 0 #FFF4A3",
            color: "forground",
            fontWeight: 800,
            fontFamily: "Inter, sans-serif",
            fontSize: "28px",
            textTransform: "uppercase",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: 0,
            marginBottom: "0",
            marginTop: "10px",
            cursor: "pointer",
            boxSizing: "border-box",
            letterSpacing: "0.5px",
          },
          typography: "Button",
          responsiveStyles: {
            tablet: { width: "100%", fontSize: "22px", height: "56px" },
            mobile: { width: "100%", fontSize: "20px", height: "48px" },
          },
          onClick: { scrollToId: "packages", behavior: "smooth" },
        },
      ],
    },
    // Claim Text
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "claim-text",
      styles: {
        textAlign: "center",
      },
      children: [
        {
          id: generateId(),
          type: "paragraph" as ElementType,
          content: "Claim Your FREE Bonuses While Supplies Last!",
          styles: {
            fontSize: "20px",
            fontFamily: "pro-display-regular-italic, sans-serif",
            fontStyle: "italic",
            color: "forground",
            margin: 0,
          },
          responsiveStyles: {
            tablet: { fontSize: "18px" },
            mobile: { fontSize: "16px" },
          },
        },
      ],
    },
  ],
});

// ====================== FAQ Section ======================
const createFAQSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  styles: {
    width: "100%",
    paddingTop: "50px",
    paddingBottom: "50px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  responsiveStyles: {
    tablet: {
      paddingTop: "30px",
      paddingBottom: "30px",
    },
    mobile: {
      paddingTop: "20px",
      paddingBottom: "20px",
    },
  },
  children: [
    {
      id: generateId(),
      type: "container" as ElementType,
      styles: {
        maxWidth: "1024px",
        margin: "0 auto",
        paddingLeft: "24px",
        paddingRight: "24px",
        width: "100%",
      },
      responsiveStyles: {
        tablet: { paddingLeft: "20px", paddingRight: "20px" },
        mobile: { paddingLeft: "16px", paddingRight: "16px" },
      },
      children: [
        // FAQ Heading
        {
          id: generateId(),
          type: "heading" as ElementType,
          content: "Frequently Asked Questions",
          styles: {
            fontSize: "46px",
            fontFamily: "SFProDisplay-Bold, sans-serif",
            fontWeight: "700",
            color: "forground",
            margin: "0 0 40px 0",
            textAlign: "center",
          },
          responsiveStyles: {
            tablet: { fontSize: "40px", marginBottom: "32px" },
            mobile: { fontSize: "32px", marginBottom: "24px" },
          },
        },
        // Accordion
        {
          id: generateId(),
          type: "accordion" as ElementType,
          allowMultiple: false,
          defaultOpen: false,
          accordionType: "single" as "single" | "multiple",
          // Icon configuration for all accordion items
          iconSrcCollapsed: "/intellident/keyboard_arrow_down.png",
          iconSrcExpanded: "/intellident/keyboard_arrow_up.png",
          // Icon styling
          iconStyles: {
            width: "24px",
            height: "24px",
            marginLeft: "16px",
          },
          styles: {
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "16px",
          },
          children: [
            // FAQ items (collapsible)
            ...[
              {
                q: "What is INTELLIDENT PRO™?",
                a: "Intellident Pro is a chewable formula designed to support oral health, fresh breath, and cognitive function by balancing the mouth's microbiome.",
              },
              {
                q: "How does INTELLIDENT PRO™ work?",
                a: "It uses clinically backed probiotics and prebiotics to eliminate harmful bacteria, strengthen gums, and promote sharper mental clarity - all in just seconds a day.",
              },
              {
                q: "Who should use INTELLIDENT PRO™?",
                a: "If you are experiencing brain fog, memory lapses, bad breath, or gum issues - this is for you! Because a sharp mind and a healthy smile never go out of style!",
              },
              {
                q: "How soon will I see results?",
                a: "Most people notice fresher breath and improved mental clarity within a few days. For optimal long-term benefits, we recommend consistent use for 60-90 days.",
              },
              {
                q: "Is INTELLIDENT PRO™ safe?",
                a: "Yes! Made with natural, research-backed ingredients in an FDA-registered, GMP-certified facility. It's gluten-free, non-GMO, and suitable for daily use.",
              },
              {
                q: "Can I take it with other supplements?",
                a: "Absolutely! Intellident Pro works well alongside other supplements. If you have specific health conditions or take medications, consult your healthcare provider.",
              },
              {
                q: "What if it doesn't work for me?",
                a: "We offer a 60-day money-back guarantee. If you're not completely satisfied, simply contact our customer support team for a full refund.",
              },
              {
                q: "How do I take INTELLIDENT PRO™ safely?",
                a: "Simply chew one tablet slowly each morning. Allow it to dissolve completely in your mouth before swallowing. That's it!",
              },
              {
                q: "Where is INTELLIDENT PRO™ made?",
                a: "It's proudly made in an FDA-registered, GMP-certified facility to ensure the highest quality and safety standards.",
              },
            ].map((item, idx) => ({
              id: generateId(),
              type: "container" as ElementType,
              styles: {
                display: "flex",
                flexDirection: "column",
                gap: "0px",
                alignItems: "flex-start",
                justifyContent: "flex-start",
                width: "100%",
                marginBottom: "16px",
              },
              children: [
                // Question heading
                {
                  id: generateId(),
                  type: "heading" as ElementType,
                  accordionRole: "question" as const,
                  content: item.q,
                  styles: {
                    margin: "0",
                    padding: "20px 24px",
                    backgroundColor: "#F5F5F5",
                    borderRadius: "8px",
                    width: "100%",
                    cursor: "pointer",
                    fontFamily: "SFProDisplay-Bold, sans-serif",
                    fontWeight: "700",
                    fontSize: "18px",
                    color: "forground",
                    display: "flex",
                    alignItems: "center",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "16px", padding: "18px 20px" },
                    mobile: { fontSize: "14px", padding: "16px 16px" },
                  },
                },
                // Answer paragraph
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  accordionRole: "answer" as const,
                  content: item.a,
                  styles: {
                    margin: "0",
                    padding: "20px 24px",
                    fontFamily: "SFProDisplay-Regular, sans-serif",
                    fontWeight: "400",
                    fontSize: "18px",
                    lineHeight: "24px",
                    color: "forground",
                    width: "100%",
                  },
                  responsiveStyles: {
                    tablet: {
                      fontSize: "16px",
                      lineHeight: "22px",
                      padding: "18px 20px",
                    },
                    mobile: {
                      fontSize: "14px",
                      lineHeight: "20px",
                      padding: "16px 16px",
                    },
                  },
                },
              ],
            })),
          ],
        },
      ],
    },
  ],
});

// ====================== Footer Section ======================
const createFooterSection = (globalConfig: GlobalConfig) => ({
  id: generateId(),
  type: "container" as ElementType,
  className: "footer-section",
  styles: {
    width: "100%",
  },
  children: [
    // Navigation Footer
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "foot",
      styles: {
        backgroundColor: "#37b4e1",
        padding: "20px 0",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "foot-container",
          styles: {
            maxWidth: "1200px",
            margin: "0 auto",
            paddingLeft: "16px",
            paddingRight: "16px",
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "foot-nav",
              styles: {
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                alignItems: "center",
                gap: "24px",
              },
              responsiveStyles: {
                tablet: { gap: "32px" },
                mobile: {
                  gap: "8px",
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                },
              },
              children: [
                {
                  id: generateId(),
                  type: "link" as ElementType,
                  className: "foot1",
                  content: "Privacy",
                  href: "/privacy",
                  styles: {
                    color: "background",
                    fontSize: "20px",
                    padding: "6px 8px",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    fontFamily: "pro-display-regular, sans-serif",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "22px" },
                    mobile: { fontSize: "16px" },
                  },
                  hoverStyles: {
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                },
                {
                  id: generateId(),
                  type: "link" as ElementType,
                  className: "foot1",
                  content: "Terms & Condition",
                  href: "/terms",
                  styles: {
                    color: "background",
                    fontSize: "20px",
                    padding: "6px 8px",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    fontFamily: "pro-display-regular, sans-serif",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "22px" },
                    mobile: { fontSize: "16px" },
                  },
                  hoverStyles: {
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                },
                {
                  id: generateId(),
                  type: "link" as ElementType,
                  className: "foot1",
                  content: "Disclaimer",
                  href: "/disclaimer",
                  styles: {
                    color: "background",
                    fontSize: "20px",
                    padding: "6px 8px",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    fontFamily: "pro-display-regular, sans-serif",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "22px" },
                    mobile: { fontSize: "16px" },
                  },
                  hoverStyles: {
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                },
                {
                  id: generateId(),
                  type: "link" as ElementType,
                  className: "foot1",
                  content: "Returns & Refunds",
                  href: "/returns",
                  styles: {
                    color: "background",
                    fontSize: "20px",
                    padding: "6px 8px",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    fontFamily: "pro-display-regular, sans-serif",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "22px" },
                    mobile: { fontSize: "16px" },
                  },
                  hoverStyles: {
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                },
                {
                  id: generateId(),
                  type: "link" as ElementType,
                  className: "foot1",
                  content: "Contact",
                  href: "/contact",
                  styles: {
                    color: "background",
                    fontSize: "20px",
                    padding: "6px 8px",
                    textDecoration: "none",
                    transition: "color 0.3s ease",
                    fontFamily: "pro-display-regular, sans-serif",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "22px" },
                    mobile: { fontSize: "16px" },
                  },
                  hoverStyles: {
                    color: "rgba(255, 255, 255, 0.9)",
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    // Disclaimer Footer
    {
      id: generateId(),
      type: "container" as ElementType,
      className: "disclaimer-footer",
      styles: {
        backgroundColor: "background",
      },
      children: [
        {
          id: generateId(),
          type: "container" as ElementType,
          className: "disclaimer-container",
          styles: {
            maxWidth: "1200px",
            margin: "0 auto",
            paddingTop: "40px",
            paddingBottom: "40px",
            paddingLeft: "8px",
            paddingRight: "8px",
          },
          responsiveStyles: {
            tablet: { paddingLeft: "8px", paddingRight: "8px" },
            mobile: { paddingLeft: "8px", paddingRight: "8px" },
          },
          children: [
            {
              id: generateId(),
              type: "container" as ElementType,
              className: "disclaimer-content",
              styles: {
                color: "#374151",
                fontSize: "16px",
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                fontFamily: "pro-display-light, sans-serif",
                fontWeight: "500",
              },
              responsiveStyles: {
                tablet: { fontSize: "16px" },
                mobile: { fontSize: "17px" },
              },
              children: [
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "These statements have not been evaluated by the Food and Drug Administration. This product is not intended to diagnose, treat, cure, or prevent any disease. The information presented on this site is for informational purposes only and is not a substitute for advice from your physician or other healthcare professional.",
                  styles: {
                    margin: 0,
                    lineHeight: "1.6",
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Always consult with a qualified healthcare provider before beginning any diet, supplement, or exercise program.",
                  styles: {
                    textAlign: "center",
                    paddingBottom: "16px",
                    margin: 0,
                    lineHeight: "1.6",
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Individual results may vary. Testimonials and reviews appearing on this site reflect real-life experiences of individuals who used our products. However, results may not be typical and are not guaranteed.",
                  styles: {
                    textAlign: "center",
                    paddingBottom: "16px",
                    margin: 0,
                    lineHeight: "1.6",
                  },
                },
                {
                  id: generateId(),
                  type: "paragraph" as ElementType,
                  content:
                    "Copyright © 2024 – 2025 Intellident Pro™. All Rights Reserved.",
                  styles: {
                    textAlign: "center",
                    fontSize: "14px",
                    color: "#374151",
                    margin: 0,
                    lineHeight: "1.6",
                  },
                  responsiveStyles: {
                    tablet: { fontSize: "16px" },
                    mobile: { fontSize: "18px" },
                  },
                },
              ],
            },
          ],
        },
      ],
    },
  ],
});

export const template4 = (globalConfig: GlobalConfig) => {
  return {
    header: createHeaderSection(globalConfig) as BuilderElement,
    hero: createHeroSection(globalConfig) as BuilderElement,
    heroV2: createIntellidentHeroSection(globalConfig) as BuilderElement,
    testimonials: createIntellidentTestimonialsSection(
      globalConfig
    ) as BuilderElement,
    features: createFeaturesSection(globalConfig) as BuilderElement,
    ingredients: createIngredientsSection(globalConfig) as BuilderElement,
    productShowcase: createProductShowcaseSection(
      globalConfig
    ) as BuilderElement,
    customerReview: createCustomerReviewSection(globalConfig) as BuilderElement,
    guarantee: createGuaranteeSection(globalConfig) as BuilderElement,
    pricingPlan: createPricingPlanSection(globalConfig) as BuilderElement,
    certificate: createNewCertificateSection(globalConfig) as BuilderElement,
    bonus: createBonusSection(globalConfig) as BuilderElement,
    faq: createFAQSection(globalConfig) as BuilderElement,
    mentioned: createMentionedInSection(globalConfig) as BuilderElement,
    partnerMenu: createPartnerMenuSection(globalConfig) as BuilderElement,
    listing: createListingSection(globalConfig) as BuilderElement,
    footer: createFooterSection(globalConfig) as BuilderElement,
  };
};

// export const template2ThankyouGlobalConfig = {
export const template4GlobalConfig = {
  typography: [
    {
      name: "Default",
      fontFamily: "SF Pro Display",
      fontSize: "16px",
      fontWeight: "400",
      fontStyle: "normal",
    },
    {
      name: "Heading-1",
      fontFamily: "SF Pro Display",
      fontSize: "60px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-2",
      fontFamily: "SF Pro Display",
      fontSize: "53px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-3",
      fontFamily: "SF Pro Display",
      fontSize: "48px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-4",
      fontFamily: "SF Pro Display",
      fontSize: "40px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-5",
      fontFamily: "SF Pro Display",
      fontSize: "32px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Heading-6",
      fontFamily: "SF Pro Display",
      fontSize: "20px",
      fontWeight: "700",
      fontStyle: "normal",
    },
    {
      name: "Body",
      fontFamily: "SF Pro Display",
      fontSize: "18px",
      fontWeight: "300",
      fontStyle: "normal",
    },
    {
      name: "Button",
      fontFamily: "SF Pro Display",
      fontSize: "28px",
      fontWeight: "800",
      fontStyle: "normal",
    },
  ],
  colors: {
    background: "#ffffff",
    forground: "#000000",
    primary: "#00AEEF",
    secondary: "#FF8E07",
    tertiary: "#F2F2F2",
    quaternary: "#051168",
    link: "#6B6B6B",
    activeLink: "#1D4ED8",
  },
  breakpoints: {
    mobileMax: 640,
    tabletMin: 641,
    tabletMax: 1024,
    desktopMin: 1025,
  },
};
